import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
/**
 * RmsFormTemplate
 * ---------------
 * What:
 *   A “one question at a time” renderer that turns a schema + answers object
 *   into a progressive, animated wizard experience (Typeform‑like).
 *
 * Why:
 *   - Reduce cognitive load for users by showing only one step at a time.
 *   - Centralize UX rules: visibility, step validation, autosave cadence.
 *   - Keep accessibility in mind with predictable focus and live region updates.
 *
 * UX notes:
 *   - We compute visible steps from answers and only allow Next when the current
 *     step is valid according to the schema validators (client‑side mirror).
 *   - We move focus to the step heading only when the step index changes so we
 *     don’t steal focus while a user is typing (common accessibility pitfall).
 *   - Progress bar is approximate (client); server remains authoritative.
 *
 * Integration:
 *   - onSaveStep is invoked on Next to persist a step patch (draft autosave).
 *   - onComplete is called from review → submit and triggers server submit flow.
 *   - initialStepId lets us restore to the last visited step on resume.
 */
import './RmsFormTemplate.css';
import { CheckCircleIcon } from '@heroicons/react/solid';
import { UserIcon } from '@heroicons/react/outline';
import TextField from './TextField';
import RadioGroup from './RadioGroup';
// import AddressField from './AddressField';
import ConfirmEmailField from './ConfirmEmailField';
import PhoneNumberField from './PhoneNumberField';
import CheckboxGroup from './CheckboxGroup';
import SubmitBar from './SubmitBar';
import { isQuestionVisible, isConditionalMet, buildValidatorIndex, validateBySchema, resolveSectionNode, getAnswerValue, applyValidator, freeTextValidationMessage } from '../utils/schemaEvaluator';
import { isLikelyUkPostcode, normalizeUkPostcode } from '../utils/postcode';
import useRmsAnalytics from '../hooks/useRmsAnalytics';
import providerLogo from '../assets/images/logos/lcs_logo.png';
import DateOfBirthPicker from './DateOfBirthPicker';
import { dobAgeRange } from '../utils/dobAgeRange';
import InfoField from './InfoField';
import MilestoneAgeGroupField from './MilestoneAgeGroupField';
import PhysicalHealthInfoGroupField from './PhysicalHealthInfoGroupField';
import { hasMeasurementProblem } from '../utils/physicalMeasurements';
import NominationField from './NominationField';
import SchoolSelectorField from './SchoolSelectorField';
import useLayoutMetrics from '../hooks/useLayoutMetrics';
import HeaderContainer from './layout/HeaderContainer';
import BrandRow from './layout/BrandRow';
import HarbourTextLink from './harbour/HarbourTextLink';
import HarbourIcon from './harbour/HarbourIcon';
import HarbourIconButton from './harbour/HarbourIconButton';
import HarbourStatusChip from './harbour/HarbourStatusChip';
import { personaliseFraming, fillSchemaChildName } from '../utils/hfpFraming';
import { personaliseSchemaWording } from '../utils/childPronouns';
import HarbourProgressSegments from './harbour/HarbourProgressSegments';
import FormHeader from './layout/FormHeader';
import QuestionViewport from './layout/QuestionViewport';
import QuestionTrack from './layout/QuestionTrack';
import QuestionCard from './layout/QuestionCard';
import useScrollController from '../hooks/useScrollController';
import { forwardWheelToActiveCard } from '../utils/cardScrollForwarding';
import ReviewList from './review/ReviewList';
import HarbourReviewList from './review/HarbourReviewList';
import HarbourButton from './harbour/HarbourButton';
import HarbourNotice from './harbour/HarbourNotice';
import FooterProgress from './layout/FooterProgress';
import RmsFormErrorBoundary from './RmsFormErrorBoundary';

// ─────────────────────────────────────────────────────────────────────────────
// ASYNC NAVIGATION CHECK TIMEOUT
// ─────────────────────────────────────────────────────────────────────────────
// The Next button is locked while an async beforeNext check runs (postcode
// eligibility, email availability). If the callable hangs — flaky connection,
// cold start that never returns, dropped request — the promise never settles,
// so without a timeout the lock is never released and the form freezes until
// a full refresh (UAT-reported bug). After this many ms we give up on the
// in-flight check, release the lock, and show a retry message so the user can
// simply press Next again.
export const NAV_CHECK_TIMEOUT_MS = 15000;
export const NAV_CHECK_RETRY_MESSAGE = 'We could not verify this just now. Please try again.';

// ─────────────────────────────────────────────────────────────────────────────
// A SAVE THE SERVER REFUSED (TDI-651)
// ─────────────────────────────────────────────────────────────────────────────
// A refused save used to read as a small "Failed to save draft. You can
// retry." under the card, so a respondent whose answers were locked kept
// answering, unaware nothing was kept. A refusal (failed-precondition: the
// answers are confirmed, or the pathway withdrawn) is permanent, so it says
// why in the server's own words and offers no retry; anything else is a
// connection failure worth trying again.
export const SAVE_FAILED_TITLE = 'Your answer wasn’t saved';

export function saveFailureFrom(error) {
  const refused = String(error?.code || '').includes('failed-precondition');
  if (refused) {
    const reason = String(error?.message || '').trim().replace(/\.$/, '');
    return {
      refused: true,
      detail: reason
        ? `${reason}.`
        : 'These answers can no longer be changed.',
    };
  }
  return { refused: false, detail: 'Check your internet connection and try again.' };
}

/**
 * Race a promise against a timeout. Rejects with a timeout error if the
 * promise does not settle within `ms`. The timer is always cleared once the
 * race settles so no stray timers linger. A late resolution of the original
 * promise after timeout is intentionally ignored by callers (the user retries
 * explicitly instead of the form advancing unexpectedly under their feet).
 */
function runWithTimeout(promise, ms) {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('Navigation check timed out')), ms);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

// Inline Saving indicator that reserves width and crossfades between states.
function SavingIndicator({ saving }) {
  const [displaySaving, setDisplaySaving] = React.useState(false);
  const startRef = React.useRef(0);
  const hideTimerRef = React.useRef(null);

  React.useEffect(() => {
    if (saving) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      startRef.current = Date.now();
      setDisplaySaving(true);
    } else {
      const elapsed = Date.now() - startRef.current;
      const remaining = Math.max(0, 500 - elapsed);
      hideTimerRef.current = setTimeout(() => {
        setDisplaySaving(false);
        hideTimerRef.current = null;
      }, remaining);
    }
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [saving]);

  return (
    <div className="relative inline-flex items-center">
      {/* Width reserver using the longer label so layout doesn't shift */}
      <span className="invisible select-none">Saving…</span>
      {/* Layered content for smooth crossfade */}
      <span className="absolute inset-0 flex items-center justify-start gap-1">
        {/* Saving layer */}
        <span className={`absolute inset-0 flex items-center justify-start gap-1 transition-opacity duration-300 ${displaySaving ? 'opacity-100' : 'opacity-0'}`}>
          <svg
            className="-ml-1 mr-3 h-5 w-5 animate-spin text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="text-sm text-gray-600">Saving…</span>
        </span>
        {/* Saved layer */}
        <span className={`absolute inset-0 flex items-center justify-start gap-1 transition-opacity duration-300 ${displaySaving ? 'opacity-0' : 'opacity-100'}`}>
          <CheckCircleIcon className="w-4 h-4 text-green-500" aria-hidden="true" />
          <span className="text-sm text-gray-600">Saved</span>
        </span>
      </span>
    </div>
  );
}

/**
 * Find the school type ('primary' | 'secondary') chosen on a schoolSelect answer.
 * The school question id is schema-defined, so we scan answer values for the
 * tagging contract written by SchoolSelectorField rather than hard-coding an id.
 * Returns null when no school has been selected yet.
 */
function findSelectedSchoolType(answers) {
  if (!answers || typeof answers !== 'object') return null;
  for (const v of Object.values(answers)) {
    if (v && typeof v === 'object' && (v.schoolType === 'primary' || v.schoolType === 'secondary')) {
      return v.schoolType;
    }
  }
  return null;
}

function RmsFormTemplateInner({
  schema: schemaProp,
  answers,
  setAnswer,
  onComplete,
  onSaveStep,
  prefersReducedMotion = false,
  initialStepId = null,
  formContext = {},
  showHome = false,
  onHome,
  analyticsContext = {},
  beforeNext,
  brandBackTo = null,
  serverSectionProgress = null,
  showBrand = true,
  // Prefill tracking: Set of question IDs that were auto-filled from previous submissions
  // These should NOT count as "answered" for progress until user confirms by clicking Next
  prefillSources = null,
  // Callback to mark questions as confirmed (no longer just prefills)
  // Called when user clicks Next on a step with prefilled data
  onConfirmPrefills = null,
  // initialMode allows callers to start directly on the Review screen (e.g., when a form is complete).
  // Valid values: 'questions' | 'review'. Defaults to 'questions' to preserve existing behavior.
  initialMode = 'questions',
  // Allows callers to customize the submit CTA label on the Review screen.
  reviewNextLabel = 'Submit',
  // Allows callers to customize the loading label while submitting on the Review screen.
  reviewLoadingLabel = 'Submitting...',
  // Allows callers to customize the breadcrumb label shown when showBrand is false.
  // Defaults to 'Back to Overview' (navigates to brandBackTo with save-then-navigate behavior).
  // This label appears when hovering the expandable user icon button.
  brandBackLabel = 'Back to Overview',
  // Optional callback invoked before navigating home/overview. If provided and returns false
  // (or a Promise resolving to false), navigation will be cancelled. Useful for showing
  // confirmation dialogs when the form has unsaved changes.
  onBeforeNavigateHome,
  // orgId and patientRecordId for NominationField status checks
  // These are used to fetch the nomination status (locked/pending) from the backend
  orgId = null,
  patientRecordId = null,
  // Indicates if the form was previously submitted. When true and the form has been edited,
  // we show an "unsaved changes" indicator to remind users they need to re-submit.
  wasSubmitted = false,
  // The respondent has confirmed and submitted all their forms (TDI-415), so
  // these answers are locked: Review renders read-only (no edit affordance,
  // no Save Changes) and the only action is back to the overview. The server
  // refuses writes regardless; this stops the UI offering one.
  locked = false,
  // The review bar's reminder of how long answers stay open. A class teacher
  // never confirms: their SENCO submits the school's forms.
  reviewBarNote = 'You can change these answers until you confirm and submit.',
  // Why the locked answers can no longer change, in the respondent's own
  // situation: a teacher never confirmed anything, their SENCO did (TDI-754).
  lockedNotice = 'You confirmed and submitted your answers, so they can no longer be changed.',
  // Harbour chrome (SEN & ND Access Portal, TDI-53): Harbour school users and
  // guardians get the Harbour look — canvas background, a slim back-link
  // header on desktop, the mobile Task app bar (Close + counter + Where line
  // + weighted progress) under 1100px, and the sticky bottom action bar. The
  // ENGINE is identical: same steps, same autosave, same validation, same
  // review. Legacy callers never receive this prop, so they are untouched.
  chrome = 'default',
  chromeBackLabel = 'All school forms',
  // Pathway tone for the Harbour progress strip: 'sen' (green) or 'referral' (blue).
  chromeTone = 'sen',
  // Who is filling the form in Harbour chrome: 'school' keeps the plum main
  // action; 'family' is a service-user surface, so the main action is gold.
  chromeAudience = 'school',
  // The child the form is about ({ firstName, pronouns }), so the framing line
  // above profile statements can name them, as the question frames do.
  subject = null,
  // Question ids already answered elsewhere (e.g. the Harbour referral wizard
  // asks for the informant once), so they are never shown as steps. Their
  // answers stay in `answers`, count as complete and are submitted as usual;
  // any of them still marked as unconfirmed prefills are confirmed on load.
  hiddenQuestionIds = null,
  // An information line shown above the form's first question (not a
  // question, never answered or counted): e.g. "Your child has been referred
  // for ADHD." on the parent's Reason for Referral (R-023, TDI-631).
  introNotice = null,
}) {
  // Harbour pages speak about the actual child: question wording, options,
  // hints and section titles use the child's pronouns (reviewed wording only).
  const subjectPronouns = subject ? subject.pronouns : undefined;
  const subjectFirstName = subject ? subject.firstName : undefined;
  // A stored [child name] placeholder (e.g. the parent's "What are you most
  // concerned about?" hint, TDI-707) is filled with the child's first name on
  // every page, after the pronoun wording so the reviewed lookup still matches.
  const schema = useMemo(
    () => fillSchemaChildName(
      chrome === 'harbour' ? personaliseSchemaWording(schemaProp, subjectPronouns) : schemaProp,
      subjectFirstName
    ),
    [schemaProp, chrome, subjectPronouns, subjectFirstName]
  );
  /**
   * isFiniteNumberLike
   * ------------------
   * Treat both real numbers and numeric strings as "valid numbers".
   *
   * Why:
   * - While the UI *writes* milestone ages as numbers, legacy data or migrations can
   *   leave numeric values as strings in Firestore (e.g., "12").
   * - Without this, users can get stuck: the UI shows a value, but validation fails
   *   and progress never reaches 100%.
   * - We keep this helper scoped to milestones only to avoid changing semantics for
   *   other question types.
   */
  const isFiniteNumberLike = useCallback((v) => {
    if (Number.isFinite(v)) return true;
    if (typeof v === 'string' && v.trim() !== '') {
      const parsed = Number(v);
      return Number.isFinite(parsed);
    }
    return false;
  }, []);

  /**
   * Validate a physicalHealthInfoGroup question.
   * Defined at the component level (rather than inside a useMemo) so it can
   * be shared between isCurrentStepValid, sectionProgress, and timeRemainingText.
   *
   * @param {Object}  q          – question definition (has measurementType)
   * @param {Object}  v          – current answer value
   * @param {boolean} requireAll – true → ALL fields + dates; false → ANY primary field
   */
  const isPhysicalHealthValid = useCallback((q, v, requireAll) => {
    if (!v || typeof v !== 'object') return false;
    const mt = q.measurementType;

    const hasNum = (n) => isFiniteNumberLike(n);
    const hasDate = (obj) =>
      obj?.dateNotSure === true || (hasNum(obj?.date?.month) && hasNum(obj?.date?.year));

    switch (mt) {
      case 'height_weight': {
        const w = v.weight || {};
        const h = v.height || {};
        const wHasValue = hasNum(w.value);
        const hHasValue = h.unit === 'ft_in' ? (hasNum(h.ft) || hasNum(h.in)) : hasNum(h.value);
        if (requireAll) return wHasValue && hHasValue && hasDate(w) && hasDate(h);
        return wHasValue || hHasValue;
      }
      case 'head_circumference':
        // Head circumference: complete if (value + date) OR notSure is true
        // When notSure is true, no date is required (guardian doesn't have this measurement)
        if (v.notSure === true) return true;
        if (requireAll) return hasNum(v.value) && hasDate(v);
        return hasNum(v.value) || v.notSure === true;
      case 'pulse_rate':
        if (requireAll) return (hasNum(v.value) || v.unable === true) && hasDate(v);
        return hasNum(v.value) || v.unable === true;
      case 'blood_pressure':
        if (requireAll) return ((hasNum(v.systolic) && hasNum(v.diastolic)) || v.unable === true) && hasDate(v);
        return hasNum(v.systolic) || hasNum(v.diastolic) || v.unable === true;
      default:
        return false;
    }
  }, [isFiniteNumberLike]);

  const resolvedSections = useMemo(() => {
    return (schema?.sections || []).map((sec) => resolveSectionNode(schema, sec, answers, formContext));
  }, [schema, answers, formContext]);

  // Dev-only logging toggle
  const debug = useMemo(() => {
    try { return typeof window !== 'undefined' && process?.env?.NODE_ENV !== 'production'; } catch (_) { return false; }
  }, []);

  const steps = useMemo(() => {
    // Build steps only from sections that are visible under current context
    const arr = [];
    (resolvedSections || []).forEach((section) => {
      // section-level gating respected later via _sectionVisibleWhen
      (section.questions || []).forEach((q) => {
        arr.push({ ...q, sectionTitle: section.title, _sectionVisibleWhen: section.visibleWhen });
      });
    });
    return arr;
  }, [resolvedSections]);

  const liveRegionRef = useRef(null);
  const headingRef = useRef(null);
  // Viewport ref (no native scrolling)
  const scrollContainerRef = useRef(null);
  const analytics = useRmsAnalytics(); // orgId handled inside hook via default; optional here
  const handleRefPhoneMetaChange = useCallback((m) => {
    setAnswer('ref_phone_meta', m);
  }, [setAnswer]);
  // Layout metrics (Phase 1)
  const { rootRef, headerRef, footerRef } = useLayoutMetrics();

  // Compute sections and currentSection early so hooks below can reference it safely
  const sections = useMemo(() => resolvedSections || [], [resolvedSections]);
  
  // Stable lookup for hiddenQuestionIds (callers may pass a fresh array).
  const hiddenIdsKey = Array.isArray(hiddenQuestionIds) ? hiddenQuestionIds.join('|') : '';
  const hiddenIdSet = useMemo(
    () => new Set(hiddenIdsKey ? hiddenIdsKey.split('|') : []),
    [hiddenIdsKey]
  );

  // Only count sections that have at least one visible question under current context
  const visibleSections = useMemo(() => {
    const secs = sections || [];
    return secs.filter((sec) => {
      // Section itself must be visible (if gated)
      const sectionVisible = isQuestionVisible(answers, sec.visibleWhen, formContext);
      if (!sectionVisible) return false;
      // And at least one child question must be visible (respecting both
      // visibleWhen and the HFP conditionalOn gate, and never a hidden one)
      const qs = (sec.questions || []);
      return qs.some((q) => !hiddenIdSet.has(q.questionId)
        && isQuestionVisible(answers, q.visibleWhen, formContext) && isConditionalMet(answers, q));
    });
  }, [sections, answers, formContext, hiddenIdSet]);

  const visibleSteps = useMemo(() => {
    // Question is visible only if the parent section is visible, the question's own
    // visibleWhen passes, and its HFP conditionalOn gate (if any) is satisfied. The
    // conditionalOn check keeps gated modifier questions (e.g. Sensory Seeking/
    // Avoidance) out of the flow until their parent gate is answered with the
    // trigger value, instead of showing them as blank, unanswerable steps.
    // Questions the caller hides (answered elsewhere) are never steps.
    return steps.filter((s) =>
      !hiddenIdSet.has(s.questionId) &&
      isQuestionVisible(answers, s._sectionVisibleWhen, formContext) &&
      isQuestionVisible(answers, s.visibleWhen, formContext) &&
      isConditionalMet(answers, s)
    );
  }, [steps, answers, formContext, hiddenIdSet]);

  // A hidden question is never reached, so its prefilled answer can never be
  // confirmed by pressing Next; confirm it here so it counts towards progress
  // and the form can reach complete.
  useEffect(() => {
    if (!hiddenIdSet.size || typeof onConfirmPrefills !== 'function') return;
    if (!(prefillSources instanceof Set)) return;
    const pending = [...hiddenIdSet].filter((qid) => prefillSources.has(qid));
    if (pending.length) onConfirmPrefills(pending);
  }, [hiddenIdSet, prefillSources, onConfirmPrefills]);
  const valIdx = useMemo(() => buildValidatorIndex(schema), [schema]);

  // ─────────────────────────────────────────────────────────────────────────────
  // DISPLAYED PROGRESS STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Progress shown in footer only updates on FORWARD NAVIGATION (not live typing).
  // This prevents confusing UX where progress goes backwards when user selects
  // an option with required free text that isn't filled yet.
  //
  // Flow:
  //   1. sectionProgress (useMemo) - live calculation for Next button validation
  //   2. displayedSectionProgress (state) - stable, only updates on forward nav
  //   3. effectiveSectionProgress - what we show in footer (prefers displayed)
  //
  // Single source of truth: when user clicks Next, we:
  //   - Update displayedSectionProgress from sectionProgress
  //   - Pass sectionProgress to backend for Firestore storage
  //   - Backend stores client value if valid, else calculates its own (redundancy)
  // ─────────────────────────────────────────────────────────────────────────────
  const [displayedSectionProgress, setDisplayedSectionProgress] = useState(null);
  
  // Initialize from server progress on first load (resume scenario)
  // Or initialize to zeros for new forms so progress doesn't update until Next click
  const hasInitializedProgressRef = useRef(false);
  useEffect(() => {
    if (hasInitializedProgressRef.current) return;
    
    // Priority 1: Use server progress if available (resume scenario)
    if (Array.isArray(serverSectionProgress) && serverSectionProgress.length > 0) {
      setDisplayedSectionProgress(serverSectionProgress);
      hasInitializedProgressRef.current = true;
      return;
    }
    
    // Priority 2: Initialize to zeros once we know the section count (new form scenario)
    // This prevents the progress bar from updating live on the first question
    const sectionCount = (visibleSections || []).length;
    if (sectionCount > 0) {
      setDisplayedSectionProgress(new Array(sectionCount).fill(0));
      hasInitializedProgressRef.current = true;
    }
  }, [serverSectionProgress, visibleSections]);

  // Ref to access latest sectionProgress in callbacks without adding to deps
  // (Defined here, populated after sectionProgress useMemo below)
  const sectionProgressRef = useRef([]);

  // Initialize controller after visibleSteps are known
  const {
    currentIndex,
    mode,
    editReturnToReview,
    liveRegionText,
    setMode,
    goNext,
    goBack,
    jumpTo,
    beginEditReturn,
    returnToReview,
  } = useScrollController({ numSteps: visibleSteps.length, headingRef, initialMode });

  const current = visibleSteps[currentIndex];

  // Visibility rules can insert or remove steps mid-flow (an answer reveals a
  // gated section). The controller tracks position by number, so an insertion
  // before the current step used to walk the reader back through questions
  // they had already answered. Pin the position to the question itself: when
  // the step list changes shape under the reader, follow their question to
  // its new index. Review mode owns its own navigation and is left alone.
  const pinnedQuestionIdRef = useRef(null);
  const pinnedIndexRef = useRef(currentIndex);
  useEffect(() => {
    const stepId = visibleSteps[currentIndex]?.questionId;
    // Navigation (the index moved) re-pins to wherever the reader went; a
    // reshape (same index, different question under it) means steps were
    // inserted or removed around them, so follow the pinned question instead.
    const navigated = currentIndex !== pinnedIndexRef.current;
    pinnedIndexRef.current = currentIndex;
    if (mode !== 'review' && !navigated
        && pinnedQuestionIdRef.current && stepId !== pinnedQuestionIdRef.current) {
      const idx = visibleSteps.findIndex((s) => s.questionId === pinnedQuestionIdRef.current);
      if (idx >= 0 && idx !== currentIndex) {
        jumpTo(idx);
        return;
      }
      // The pinned question itself was removed by the reshape. If the index
      // now points past the end of the list, clamp to the last step so the
      // reader never faces an empty page.
      if (idx === -1 && currentIndex >= visibleSteps.length) {
        jumpTo(visibleSteps.length - 1);
        return;
      }
    }
    if (stepId) pinnedQuestionIdRef.current = stepId;
  }, [visibleSteps, currentIndex, mode, jumpTo]);

  const currentSection = useMemo(() => {
    if (!current) return null;
    const found = (visibleSections || []).find((sec) => (sec.questions || []).some((q) => q.questionId === current.questionId));
    return found || null;
  }, [visibleSections, current]);

  // HFP forms display a framing question above each statement to give context
  // to the rating scale. All HFP domains use the same framing except Sensory
  // Experiences which asks about frequency rather than capability.
  const HFP_FORM_KEY_SET = useMemo(() => new Set([
    'hfpThinkingAndLearning',
    'hfpAttentionAndMemory',
    'hfpEnergyAndMotivation',
    'hfpEmotionsAndRelationships',
    'hfpLanguageAndCommunication',
    'hfpMovementAndCoordination',
    'hfpSensoryExperiences',
    'hfpDailyLifeParticipation',
  ]), []);

  const questionPreamble = useMemo(() => {
    if (!current || current.type !== 'radio') return '';
    // HFP v2/v3: framing is authored per item in the schema, so any form works
    // without a hardcoded key list. Harbour pages name the child (the question
    // frames' wording); every page hides authoring notes stored in the slot.
    const perItemFraming = current.meta?.hfp?.framingQuestion;
    if (perItemFraming) {
      return personaliseFraming(perItemFraming, subject || {}, {
        personaliseGeneric: chrome === 'harbour',
      });
    }
    // HFP v1 fallback: framing is fixed per form.
    const formKey = schema?.formKey;
    if (!formKey || !HFP_FORM_KEY_SET.has(formKey)) return '';
    if (formKey === 'hfpSensoryExperiences') {
      return 'Compared to other children their age, how often do they do the following?';
    }
    return 'Compared to other children their age, how do they manage the following?';
  }, [schema?.formKey, current, HFP_FORM_KEY_SET, subject, chrome]);

  const didApplyInitialRef = useRef(false);
  useEffect(() => {
    // Align to initialStepId once (resume). Avoid jumping back on every answers change.
    if (!didApplyInitialRef.current && initialStepId && visibleSteps.length) {
      const found = visibleSteps.findIndex(s => s.questionId === initialStepId);
      if (found >= 0) {
        // TDI-508: the saved step is the last one VISITED, which after
        // answering it and leaving is a question already done. Landing there
        // made every return to a long form start with a pointless Next. If
        // the saved step is answered, carry on from the first unanswered one
        // after it; if it is not, stay put, because a half-finished question
        // is exactly where someone meant to come back to.
        const isAnswered = (step) => {
          const value = getAnswerValue(answers, step?.questionId);
          if (value === null || value === undefined) return false;
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === 'object') return Object.keys(value).length > 0;
          return String(value).trim() !== '';
        };
        let target = found;
        if (isAnswered(visibleSteps[found])) {
          const nextUnanswered = visibleSteps.findIndex(
            (step, i) => i > found && !isAnswered(step),
          );
          if (nextUnanswered >= 0) target = nextUnanswered;
        }
        jumpTo(target);
        didApplyInitialRef.current = true;
      }
    }
  }, [visibleSteps, visibleSteps.length, initialStepId, jumpTo, answers]);

  // Live region text provided by controller
  useEffect(() => {
    if (liveRegionRef.current && current) {
      liveRegionRef.current.textContent = `Question: ${current.label || current.sectionTitle}. ${liveRegionText}`;
    }
  }, [current, liveRegionText]);

  // Smoothly scroll to the active step on index change or when returning from review
  // No native scrolling in Phase 3; track handles transitions.

  // Emit analytics step_viewed on step change.
  //
  // The payload reads through a ref rather than depending on `analytics` and
  // `analyticsContext` directly: callers pass the context as an object literal,
  // so its identity changes on every render, and depending on it re-ran this
  // effect for every keystroke. That recorded one step view per character typed,
  // which inflated the most frequent event in the funnel and made per-step
  // comparisons meaningless (text steps inflate far more than choice steps).
  // The dependency list is now exactly what identifies a step.
  const stepViewedPayloadRef = useRef({ analytics, editReturnToReview, analyticsContext });
  stepViewedPayloadRef.current = { analytics, editReturnToReview, analyticsContext };

  useEffect(() => {
    if (mode !== 'questions') return;
    if (!current?.questionId) return;
    const { analytics: tracker, editReturnToReview: returning, analyticsContext: context } =
      stepViewedPayloadRef.current;
    tracker.stepViewed({
      stepId: current.questionId,
      sectionId: currentSection?.sectionId,
      formKey: schema?.formKey,
      editReturnToReview: returning,
      ...context,
    });
  }, [currentIndex, mode, current?.questionId, currentSection?.sectionId, schema?.formKey]);

  // Removed unused percent bar in favor of segmented footer + minutes estimate

  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const [lastSave, setLastSave] = useState(null);
  // Navigation lock while awaiting postcode eligibility
  const [navLocked, setNavLocked] = useState(false);
  // Inline message when an async beforeNext check (postcode/email) fails or
  // times out. The lock is released alongside setting this, so the user can
  // retry by pressing Next again instead of being frozen on the step.
  const [navCheckError, setNavCheckError] = useState(null);
  // Review submit loading state
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  // Review submit error. When submit fails we MUST keep the user on the review screen
  // and tell them, so a failed submit can't silently leave the form as a 100% draft
  // (which then reads as "not filled in"). Cleared on each new attempt.
  const [reviewSubmitError, setReviewSubmitError] = useState(null);

  // Harbour review on desktop: the question being changed opens in a panel
  // beside the list (3254:157697, 3455:14636); phones keep the question page.
  const [reviewEditIndex, setReviewEditIndex] = useState(null);
  const [reviewUpdatedIndex, setReviewUpdatedIndex] = useState(null);
  const reviewEditStartRef = useRef(null);
  const [isWideReview, setIsWideReview] = useState(() => (
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(min-width: 1100px)').matches
  ));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(min-width: 1100px)');
    const onChange = () => setIsWideReview(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); };
  }, []);
  
  // ══════════════════════════════════════════════════════════════════════════
  // EDIT MODE FOR SUBMITTED FORMS
  // ══════════════════════════════════════════════════════════════════════════
  // When editing a previously submitted form, we hold changes in local state
  // only (no auto-save to Firestore). The user must explicitly click "Save Changes"
  // in Review mode to commit their edits. This prevents unintended side-effects
  // (like notifications) and gives users clear control over their submissions.
  //
  // Key behaviors when isEditingSubmittedForm=true:
  //   1. maybeSaveCurrentStep() is a no-op (no Firestore writes)
  //   2. Unmount cleanup doesn't save (no accidental persistence)
  //   3. Navigation shows confirmation dialog (Phase 2)
  //   4. Review mode shows Save/Discard buttons (Phase 3)
  // ══════════════════════════════════════════════════════════════════════════
  
  // Track if a previously submitted form has been edited (needs re-submission)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Capture the initial answers snapshot when form loads (for change detection and revert)
  const initialAnswersRef = useRef(null);
  
  // Ref to track wasSubmitted for use in unmount effect (avoids stale closure)
  const wasSubmittedRef = useRef(wasSubmitted);
  useEffect(() => { wasSubmittedRef.current = wasSubmitted; }, [wasSubmitted]);
  
  // Derived state: are we currently editing a submitted form?
  // This is the primary flag that controls edit-mode behavior
  const isEditingSubmittedForm = wasSubmitted && hasUnsavedChanges;
  
  // Ref for isEditingSubmittedForm to use in unmount effect
  const isEditingSubmittedFormRef = useRef(false);
  useEffect(() => { isEditingSubmittedFormRef.current = isEditingSubmittedForm; }, [isEditingSubmittedForm]);
  
  // Store initial answers on first load (for submitted form edit detection and revert)
  useEffect(() => {
    if (wasSubmitted && initialAnswersRef.current === null && answers && Object.keys(answers).length > 0) {
      // Deep clone to prevent reference mutation
      initialAnswersRef.current = JSON.parse(JSON.stringify(answers));
    }
  }, [wasSubmitted, answers]);
  
  // Detect changes to submitted forms
  useEffect(() => {
    if (!wasSubmitted || !initialAnswersRef.current) return;
    
    // Compare current answers with initial snapshot
    const changed = JSON.stringify(answers) !== JSON.stringify(initialAnswersRef.current);
    setHasUnsavedChanges(changed);
  }, [wasSubmitted, answers]);
  
  // Discard changes: revert answers to the initial snapshot
  // Used when user clicks "Discard" in Review mode for a submitted form
  const discardChanges = useCallback(() => {
    if (!initialAnswersRef.current) return;
    
    // Revert each answer to its initial value
    const initial = initialAnswersRef.current;
    Object.keys(initial).forEach((key) => {
      setAnswer(key, initial[key]);
    });
    
    // Also clear any new keys that weren't in the original
    Object.keys(answers).forEach((key) => {
      if (!(key in initial)) {
        setAnswer(key, undefined);
      }
    });
    
    setHasUnsavedChanges(false);
  }, [answers, setAnswer]);
  
  // Removed home-saving navigation in new header; retain save on review/step transitions
  // Edit Answers mode handled by useScrollController in Phase 3
  // Track the last subset saved per step, and the subset when the user entered the step
  const lastSavedSubsetByStepIdRef = useRef({});
  const stepEntrySubsetByStepIdRef = useRef({});
  // Cache the last postcode that was eligibility-checked (normalized), so we only re-check when it changes
  const lastCheckedPostcodeRef = useRef('');
  // Track optional steps the user has explicitly considered (advanced past), so they count towards progress
  const [consideredOptionalStepIds, setConsideredOptionalStepIds] = useState(() => new Set());

  const deepEqual = useCallback((a, b) => {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (_e) {
      return false;
    }
  }, []);

  const buildStepSubset = useCallback((step) => {
    if (!step) return {};
    // Info type has no data to save
    if (step.type === 'info') {
      return {};
    }
    if (step.type === 'group') {
      const sub = {};
      (step.children || []).forEach(ch => { sub[ch.questionId] = answers[ch.questionId]; });
      return sub;
    }
    if (step.type === 'nomination') {
      const sub = { [step.questionId]: answers[step.questionId] };
      if (step.meta?.requireConsent) {
        const consentKey = `${step.questionId}__consent`;
        sub[consentKey] = answers[consentKey];
      }
      return sub;
    }
    if (step.type === 'address' || step.type === 'repeatable_group' || step.type === 'milestoneAgeGroup' || step.type === 'physicalHealthInfoGroup' || step.type === 'schoolSelect') {
      return { [step.questionId]: answers[step.questionId] };
    }
    if (['radio','checkboxes','text','textArea','email','email_confirm','phone','phone_confirm','date'].includes(step.type)) {
      return { [step.questionId]: answers[step.questionId] };
    }
    return {};
  }, [answers]);

  // On step change, snapshot the subset when the user "enters" a step.
  // Depend on `current` and `buildStepSubset` to satisfy linting and update when either changes.
  useEffect(() => {
    if (current?.questionId) {
      stepEntrySubsetByStepIdRef.current[current.questionId] = buildStepSubset(current);
    }
  }, [current, buildStepSubset]);

  // What the server last held for each step: the subset as it stood when the
  // step was ENTERED, taken once per entry (the ref above refreshes with every
  // keystroke, so it cannot tell a saved answer from one just picked). This is
  // what "Answer saved" may claim, and what a refused save falls back to
  // (TDI-754): the pill said "Answer saved" for saves the server refused.
  const stepPersistedSubsetByStepIdRef = useRef({});
  const lastEnteredStepIdRef = useRef(null);
  useEffect(() => {
    const qid = current?.questionId;
    if (qid && lastEnteredStepIdRef.current !== qid) {
      lastEnteredStepIdRef.current = qid;
      stepPersistedSubsetByStepIdRef.current[qid] = buildStepSubset(current);
    }
  }, [current, buildStepSubset]);

  /**
   * maybeSaveCurrentStep - Save step and update displayed progress on navigation
   * 
   * Called when user navigates (Next, Back, or Overview). Handles:
   * 
   *   1. Saving the step patch to Firestore (if changes detected OR forcePositionSave)
   *   2. Confirming any prefilled values
   *   3. Updating displayedSectionProgress (so footer only updates on forward nav)
   *   4. Passing current sectionProgress to backend for persistence
   *   5. Updating lastStepId so user returns to this question on resume
   * 
   * The displayed progress update happens AFTER successful save, ensuring:
   *   - Progress never goes backwards while user is typing/selecting
   *   - Firestore and frontend stay in sync (single source of truth)
   *   - Smooth UX without jarring progress bar changes
   * 
   * @param step - The current question step
   * @param options.forcePositionSave - If true, always save to update lastStepId even if no answer changes.
   *   Used for Back navigation and Overview exit so user returns to exactly this question.
   * @param options.updateProgress - If true, update displayedSectionProgress after save. Default true.
   *   Set to false for Back navigation (progress shouldn't change when going back).
   */
  const maybeSaveCurrentStep = useCallback((step, options = {}) => {
    const { forcePositionSave = false, updateProgress = true } = options;
    
    if (!step || !onSaveStep) return;
    
    // ────────────────────────────────────────────────────────────────────────
    // EDIT MODE: Skip Firestore save for submitted forms being edited
    // ────────────────────────────────────────────────────────────────────────
    // When editing a previously submitted form, we hold changes in local state
    // only. The user must explicitly click "Save Changes" in Review mode to
    // persist their edits to Firestore. This prevents:
    //   - Unintended side-effects (notifications, scoring recalculation)
    //   - Confusion about what's been "saved" vs "submitted"
    //   - Accidental partial saves when navigating between questions
    //
    // Local state (answers) is still updated via setAnswer(), so the UI reflects
    // the user's edits. Progress calculations also work locally.
    // ────────────────────────────────────────────────────────────────────────
    if (isEditingSubmittedFormRef.current) {
      // Still update displayed progress locally for visual feedback
      if (updateProgress) {
        const currentProgress = sectionProgressRef.current;
        if (Array.isArray(currentProgress) && currentProgress.length > 0) {
          setDisplayedSectionProgress([...currentProgress]);
        }
      }
      // Skip Firestore save - changes held in local state only
      return;
    }
    
    const subset = buildStepSubset(step);
    const stepId = step.questionId;
    const lastSaved = lastSavedSubsetByStepIdRef.current[stepId];
    const atEntry = stepEntrySubsetByStepIdRef.current[stepId];
    const hasChangedSinceLastSave = !deepEqual(subset, lastSaved);
    const hasChangedSinceEntry = !deepEqual(subset, atEntry);
    const hasAnswerChanges = hasChangedSinceLastSave || hasChangedSinceEntry;
    
    // Collect question IDs that were saved in this step (for prefill confirmation)
    const savedQuestionIds = Object.keys(subset);
    
    // Helper to update displayed progress (called on successful forward navigation)
    // Uses ref to get latest calculated progress without adding to callback deps
    const doUpdateDisplayedProgress = () => {
      if (!updateProgress) return;
      const currentProgress = sectionProgressRef.current;
      if (Array.isArray(currentProgress) && currentProgress.length > 0) {
        setDisplayedSectionProgress([...currentProgress]);
      }
    };
    
    // Skip network save if:
    // 1. No answer changes AND
    // 2. Not forcing position save (for Back/Overview navigation)
    if (!hasAnswerChanges && !forcePositionSave) {
      // No changes to save, but still navigating forward
      // Confirm prefills and update displayed progress
      if (typeof onConfirmPrefills === 'function' && savedQuestionIds.length > 0) {
        onConfirmPrefills(savedQuestionIds);
      }
      doUpdateDisplayedProgress();
      return;
    }
    
    setLastSave({ step, patch: subset });
    setSaving(true);
    (async () => {
      try {
        // Pass current sectionProgress to backend for persistence (single source of truth)
        // Backend stores this value if valid, else calculates its own as fallback
        const progressToSave = sectionProgressRef.current;
        await onSaveStep(step, subset, progressToSave);
        lastSavedSubsetByStepIdRef.current[stepId] = subset;
        setSavedTick((n) => n + 1);
        setSaveError(null);

        // Mark these questions as confirmed (no longer just prefills)
        if (typeof onConfirmPrefills === 'function' && savedQuestionIds.length > 0) {
          onConfirmPrefills(savedQuestionIds);
        }
        
        // Update displayed progress AFTER successful save (if requested)
        doUpdateDisplayedProgress();
        
        try {
          const secId = currentSection?.sectionId;
          analytics.stepSaved({ stepId: step?.questionId, sectionId: secId, formKey: schema?.formKey, editReturnToReview, ...analyticsContext });
        } catch (_e) {}
      } catch (e) {
        const failure = saveFailureFrom(e);
        setSaveError(failure);
        // A refusal is permanent (the answers are locked), so the screen must
        // not keep showing an answer the server will never hold: put back
        // what was last saved, or what the step held when it was entered
        // (TDI-754).
        if (failure.refused && typeof setAnswer === 'function') {
          const prior = lastSavedSubsetByStepIdRef.current[stepId]
            || stepPersistedSubsetByStepIdRef.current[stepId]
            || {};
          for (const key of Object.keys(subset)) {
            setAnswer(key, prior[key]);
          }
        }
        // Don't update displayed progress on failure - keeps UI consistent
      } finally {
        setSaving(false);
      }
    })();
  }, [onSaveStep, onConfirmPrefills, buildStepSubset, analytics, currentSection?.sectionId, schema?.formKey, editReturnToReview, analyticsContext, deepEqual, setAnswer]);
  // Harbour question frames: a single-choice answer saves when it is picked and
  // the card says so ("Answer saved"). The step save skips unchanged answers,
  // so the save on Next that follows is a no-op rather than a second write.
  const currentAnswerValue = current ? answers?.[current.questionId] : undefined;
  useEffect(() => {
    if (chrome !== 'harbour' || !current || current.type !== 'radio') return;
    if (currentAnswerValue === undefined || currentAnswerValue === null || currentAnswerValue === '') return;
    maybeSaveCurrentStep(current, { updateProgress: false });
    // Saving is keyed to the picked value only; the save function itself is stable per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chrome, current?.questionId, JSON.stringify(currentAnswerValue)]);

  // The save status as the question frames show it: a caption until the answer
  // is saved, then the "Answer saved" pill in the pathway colour.
  const renderHarbourSaveStatus = () => {
    if (!harbourSaveStatus) return null;
    if (harbourSaveStatus.kind === 'saved') {
      return <HarbourStatusChip status="done" tone={chromeTone} label="Answer saved" />;
    }
    return (
      <span className="harbour-save-caption" role="status" aria-live="polite">
        {harbourSaveStatus.kind === 'saving' ? 'Saving' : 'Your answer saves automatically'}
      </span>
    );
  };

  const harbourSaveStatus = useMemo(() => {
    if (chrome !== 'harbour' || !current) return null;
    const answered = !(currentAnswerValue === undefined || currentAnswerValue === null || currentAnswerValue === ''
      || (Array.isArray(currentAnswerValue) && currentAnswerValue.length === 0));
    if (saving) return { kind: 'saving' };
    if (answered) {
      // "Answer saved" only once the server holds it: a successful save this
      // session, or the answer the step was entered with. The live entry ref
      // refreshes with every pick, so it must not vouch here (TDI-754).
      const subset = buildStepSubset(current);
      const saved = deepEqual(subset, lastSavedSubsetByStepIdRef.current[current.questionId])
        || deepEqual(subset, stepPersistedSubsetByStepIdRef.current[current.questionId]);
      if (saved) return { kind: 'saved' };
    }
    return { kind: 'caption' };
    // savedTick re-derives after each save (the saved snapshot lives in a ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chrome, current, currentAnswerValue, saving, savedTick, buildStepSubset, deepEqual]);

  // Best-effort save on unmount/route-away if the latest step has unsaved changes.
  // Use refs to capture the latest values without re-subscribing the effect,
  // keeping React hook dependencies satisfied without extra interim cleanups.
  const latestStepRef = useRef(current);
  const latestMaybeSaveRef = useRef(maybeSaveCurrentStep);
  useEffect(() => { latestStepRef.current = current; }, [current]);
  useEffect(() => { latestMaybeSaveRef.current = maybeSaveCurrentStep; }, [maybeSaveCurrentStep]);
  // Best-effort save on unmount/route-away - force position save so user returns to this question
  // EXCEPTION: When editing a submitted form, we DON'T auto-save on unmount.
  // The user must explicitly click "Save Changes" to persist their edits.
  useEffect(() => {
    return () => {
      try {
        // Skip auto-save if we're editing a submitted form
        // (user needs to explicitly save or discard changes)
        if (isEditingSubmittedFormRef.current || wasSubmittedRef.current) {
          return;
        }
        
        const step = latestStepRef.current;
        if (step) {
          // Force position save so lastStepId is updated even if no answer changes
          latestMaybeSaveRef.current?.(step, { forcePositionSave: true, updateProgress: false });
        }
      } catch (_) {}
    };
  }, []);
  
  // Browser navigation warning for unsaved changes in submitted forms
  // This warns users when they try to close the tab or navigate away without re-submitting
  useEffect(() => {
    if (!wasSubmitted || !hasUnsavedChanges) return;
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Modern browsers require returnValue to be set
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [wasSubmitted, hasUnsavedChanges]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // NAVIGATION PROTECTION - Unsaved changes handling
  // ══════════════════════════════════════════════════════════════════════════
  // For submitted forms with unsaved changes:
  // - Browser close/refresh: Handled by beforeunload event above
  // - In-app navigation: User must explicitly use "Save Changes" or "Discard"
  //   buttons in the Review mode banner before navigating away
  //
  // Note: React Router's useBlocker requires data routers (createBrowserRouter)
  // which this app doesn't use. The beforeunload handler + explicit UI actions
  // provide adequate protection for the "Confirm to Update" pattern.
  // ══════════════════════════════════════════════════════════════════════════
  // Orchestrate step advancement with an optional postcode eligibility guard
  const handleGuardedAdvance = useCallback(async (advanceKind) => {
    // Each attempt starts clean: a previous failure/timeout message should not
    // linger once the user retries.
    setNavCheckError(null);
    // Detect steps that require async validation with loading state
    const isSubjectAddress =
      !!current &&
      (
        current.questionId === 'subject_address' ||
        (Array.isArray(current.children) && current.children.some((ch) => ch?.questionId === 'addr_postcode'))
      );
    
    // Email step also requires async validation (checking if email is already registered)
    const isEmailStep = !!current && current.questionId === 'ref_email_group';
    
    // Either address or email steps require loading state during validation
    const requiresLoadingState = isSubjectAddress || isEmailStep;
    
    // Helper to perform the actual navigation after success
    const performAdvance = () => {
      // Fire-and-forget draft save (only if changes)
      if (current) maybeSaveCurrentStep(current);
      // If the current step is optional, count it as "considered" once the user advances
      try {
        if (current && current.required !== true) {
          setConsideredOptionalStepIds((prev) => {
            const next = new Set(prev);
            next.add(current.questionId);
            return next;
          });
        }
      } catch (_) {}
      if (advanceKind === 'to_review') {
        setMode('review');
        return;
      }
      if (advanceKind === 'return_to_review') {
        returnToReview();
        return;
      }
      // default: go to next step
      goNext();
    };
    
    // Non-guarded path (no loading state needed)
    if (!requiresLoadingState) {
      // Optional guard (could be used by other steps) without banner/lock
    if (typeof beforeNext === 'function' && current) {
      try {
        const decision = await beforeNext(current, answers);
          if (decision && decision.proceed === false) return;
          if (decision && decision.redirectTo) {
            window.location.assign(decision.redirectTo);
            return;
          }
        } catch (_e) {
          // Previously this swallowed the failure silently — the user pressed
          // Next and nothing happened, which reads as a freeze. Surface a
          // retry message instead.
          setNavCheckError(NAV_CHECK_RETRY_MESSAGE);
          return;
        }
      }
      performAdvance();
      return;
    }
    
    // ─────────────────────────────────────────────────────────────────────────
    // Email step: Check email availability with loading state
    // ─────────────────────────────────────────────────────────────────────────
    if (isEmailStep) {
      try {
        setNavLocked(true);
        // Bound the check with a timeout: a hung callable must never leave the
        // Next button locked forever (form-freeze bug). On timeout the race
        // rejects, we fall through to the catch below, and the user can retry.
        const decision = typeof beforeNext === 'function' && current
          ? await runWithTimeout(beforeNext(current, answers), NAV_CHECK_TIMEOUT_MS)
          : { proceed: true };

        if (decision && decision.proceed === false) {
          setNavLocked(false);
          return;
        }
        if (decision && decision.redirectTo) {
          setNavLocked(false);
          window.location.assign(decision.redirectTo);
          return;
        }
        // Success → immediately advance
        setNavLocked(false);
        performAdvance();
      } catch (_e) {
        // Failed or timed-out check: release the lock so Next is usable again
        // and tell the user — do not advance.
        setNavLocked(false);
        setNavCheckError(NAV_CHECK_RETRY_MESSAGE);
        return;
      }
      return;
    }
    // Guarded path for postcode eligibility — only check if not already checked for the same postcode value
    try {
      // Extract current postcode from answers and normalize for stable comparison
      const extractPostcode = (stepNode) => {
        if (!stepNode) return '';
        if (stepNode.questionId === 'subject_address') {
          const v = answers?.subject_address;
          return String(v?.addr_postcode || '');
        }
        if (Array.isArray(stepNode.children) && stepNode.children.some((ch) => ch?.questionId === 'addr_postcode')) {
          return String(answers?.['addr_postcode'] || '');
        }
        return '';
      };
      const rawPostcode = extractPostcode(current);
      const normalizedPostcode = normalizeUkPostcode(rawPostcode || '');
      // If we have a previously checked postcode and it hasn't changed, skip the check
      if (normalizedPostcode && lastCheckedPostcodeRef.current === normalizedPostcode) {
        performAdvance();
        return;
      }
      // If no postcode (should be blocked by validation anyway), just proceed without checking
      if (!normalizedPostcode) {
        performAdvance();
        return;
      }
      setNavLocked(true);
      // Bound the eligibility check with a timeout so a hung callable cannot
      // leave the form frozen on "Checking postcode..." until refresh.
      const decision = typeof beforeNext === 'function' && current
        ? await runWithTimeout(beforeNext(current, answers), NAV_CHECK_TIMEOUT_MS)
        : { proceed: true };
        if (decision && decision.proceed === false) {
          // Ineligible: deliberately do NOT record the postcode as checked.
          // Recording it here would let the next press of Next hit the
          // "already checked" short-circuit above and advance PAST the
          // eligibility gate with an ineligible postcode. Leaving it unmarked
          // means every retry with the same postcode re-runs the check and
          // re-surfaces the caller's ineligibility dialog/banner.
        setNavLocked(false);
          return;
        }
        if (decision && decision.redirectTo) {
        setNavLocked(false);
          window.location.assign(decision.redirectTo);
          return;
        }
      // Success (proceed) → only NOW mark this postcode as eligibility-checked
      // so we don't re-check until it changes, then advance. Timed-out/failed
      // checks never reach this line either (they throw into the catch below),
      // so retrying after a failure also re-runs the check.
      lastCheckedPostcodeRef.current = normalizedPostcode;
      setNavLocked(false);
      performAdvance();
    } catch (_e) {
      // Failed or timed-out check: release the lock so Next is usable again
      // and tell the user — do not advance.
      setNavLocked(false);
      setNavCheckError(NAV_CHECK_RETRY_MESSAGE);
        return;
      }
  }, [current, beforeNext, answers, maybeSaveCurrentStep, goNext, setMode, returnToReview]);

  // A stale failure message should not follow the user to a different step
  // (e.g. after pressing Back).
  useEffect(() => {
    setNavCheckError(null);
  }, [currentIndex]);

  const onNext = async () => {
    // Decide final destination (review if last; otherwise next)
    const isLast = currentIndex >= visibleSteps.length - 1;
    const advanceKind = isLast ? 'to_review' : 'next';
    await handleGuardedAdvance(advanceKind);
  };

  const retryLastSave = useCallback(() => {
    if (!lastSave || !onSaveStep) return;
    setSaving(true);
    (async () => {
      try {
        await onSaveStep(lastSave.step, lastSave.patch);
        setSaveError(null);
      } catch (e) {
        setSaveError(saveFailureFrom(e));
      } finally {
        setSaving(false);
      }
    })();
  }, [lastSave, onSaveStep]);

  const onBack = () => {
    if (mode === 'review') {
      setMode('questions');
      jumpTo(visibleSteps.length - 1);
      return;
    }
    // Force position save so user returns to this question on resume (even if no answer changes)
    // Don't update progress on back navigation (progress only updates on forward nav)
    if (current) maybeSaveCurrentStep(current, { forcePositionSave: true, updateProgress: false });
    goBack();
  };
  // Harbour review panel: open a question beside the list, save it on close,
  // and mark the row "Answer updated" when the answer changed.
  const closeReviewEdit = () => {
    const idx = reviewEditIndex;
    if (idx == null) return;
    const step = visibleSteps[idx];
    if (step) {
      maybeSaveCurrentStep(step, { updateProgress: false });
      setReviewUpdatedIndex(deepEqual(buildStepSubset(step), reviewEditStartRef.current) ? null : idx);
    }
    setReviewEditIndex(null);
  };
  const openReviewEdit = (i) => {
    if (!isWideReview) {
      beginEditReturn(i);
      return;
    }
    if (reviewEditIndex != null) closeReviewEdit();
    const step = visibleSteps[i];
    reviewEditStartRef.current = step ? buildStepSubset(step) : null;
    jumpTo(i);
    setReviewEditIndex(i);
  };

  const onReturnToReviewGuarded = async () => {
    // When editing from Review, we still need to re-check eligibility on address edits
    await handleGuardedAdvance('return_to_review');
  };

  // Closing a question opened from "Review your answers" goes back to the
  // review, never out of the form (TDI-802, R-055): the person is changing
  // one answer, not leaving. Save what they picked on the way back so the
  // row shows it.
  const closeEditReturn = () => {
    if (current) {
      try { maybeSaveCurrentStep(current, { updateProgress: false }); } catch (_e) { /* best-effort save */ }
    }
    returnToReview();
  };

  // Save-then-navigate handler for "Overview" breadcrumb (must be defined before any early returns)
  // If onBeforeNavigateHome is provided and returns false, navigation is cancelled.
  // Navigation is optimistic - we don't wait for the save to complete before navigating.
  // This provides snappy UX; the save happens in the background (fire-and-forget).
  const handleNavigateOverview = useCallback(async () => {
    // Check with caller if navigation should proceed (e.g., show confirmation dialog)
    if (typeof onBeforeNavigateHome === 'function') {
      try {
        const shouldProceed = await onBeforeNavigateHome();
        if (shouldProceed === false) {
          return; // Navigation cancelled by caller
        }
      } catch (_e) {
        // If callback throws, proceed with navigation anyway
      }
    }
    
    // Fire-and-forget save: don't await, navigate immediately for snappy UX
    // Force position save so user returns to this exact question on resume
    // This saves both the current answers AND updates lastStepId in Firestore
    if (current) {
      try {
        maybeSaveCurrentStep(current, { forcePositionSave: true, updateProgress: false });
      } catch (_e) {
        // Ignore save errors - user is navigating away
      }
    }
    
    // Navigate immediately without waiting for save
    if (brandBackTo) {
      try {
        window.location.assign(brandBackTo);
      } catch (_e) {}
    }
  }, [current, maybeSaveCurrentStep, brandBackTo, onBeforeNavigateHome]);

  // Removed home navigation button from header in the new layout

  const renderStepNode = (node, answersScope = answers) => {
    if (!node) return null;
    switch (node.type) {
      case 'group':
        return (
          <div className="space-y-4 text-left">
            {(node.children || []).map((child) => {
              if (child.type === 'text' || child.type === 'email' || child.type === 'textArea') {
                const fieldValid = validateBySchema({ schema, question: child, answers: answersScope, valIdx });
                return (
                  <TextField
                    key={child.questionId}
                    id={child.questionId}
                    label={child.label}
                    type={child.type === 'email' ? 'email' : (child.type === 'textArea' ? 'textArea' : 'text')}
                    required={child.required}
                    value={answersScope[child.questionId]}
                    validOverride={answersScope[child.questionId] ? fieldValid : undefined}
                    onChange={(v) => {
                      // If editing primary email/phone, clear the corresponding confirm field
                      if (child.questionId === 'ref_email') {
                        setAnswer('ref_email_confirm', '');
                      }
                      if (child.questionId === 'ref_phone') {
                        setAnswer('ref_phone_confirm', '');
                      }
                      setAnswer(child.questionId, v);
                    }}
                  />
                );
              }
              if (child.type === 'email_confirm') {
                const fieldValid = validateBySchema({ schema, question: child, answers: answersScope, valIdx });
                const primaryChild = (node.children || []).find(ch => ch.questionId === 'ref_email');
                const primaryValid = primaryChild ? validateBySchema({ schema, question: primaryChild, answers: answersScope, valIdx }) : !!answersScope['ref_email'];
                return (
                  <ConfirmEmailField
                    key={child.questionId}
                    id={child.questionId}
                    label={child.label}
                    required={child.required}
                    originalValue={answersScope['ref_email']}
                    value={answersScope[child.questionId]}
                    validOverride={answersScope[child.questionId] ? fieldValid : undefined}
                    onChange={(v) => setAnswer(child.questionId, v)}
                    disabled={!primaryValid}
                  />
                );
              }
              if (child.type === 'phone') {
                return (
                  <PhoneNumberField
                    key={child.questionId}
                    label={child.label}
                    required={child.required}
                    rawValue={answersScope['ref_phone_raw']}
                    onRawChange={(v) => { setAnswer('ref_phone_raw', v); }}
                    onNormalizedChange={(e164) => { setAnswer('ref_phone', e164); setAnswer('ref_phone_confirm', e164); }}
                    onMetaChange={handleRefPhoneMetaChange}
                  />
                );
              }
              if (child.type === 'phone_confirm') {
                // Remove confirm phone UI; auto-fill handled when primary phone normalizes
                return null;
              }
              return null;
            })}
          </div>
        );
      case 'repeatable_group': {
        const items = Array.isArray(answers[node.questionId]) ? answers[node.questionId] : [];
        const minItems = Number(node.minItems || 0);
        const maxItems = Number.isFinite(node.maxItems) ? Number(node.maxItems) : Infinity;
        const canAdd = items.length < maxItems;
        const onAdd = () => {
          const newRow = { __key: `${Date.now()}_${Math.random().toString(36).slice(2)}` };
          (node.children || []).forEach(ch => { newRow[ch.questionId] = ''; });
          setAnswer(node.questionId, [...items, newRow]);
        };
        const onRemove = (rowIdx) => {
          if (items.length <= minItems) return;
          const next = items.filter((_, i) => i !== rowIdx);
          setAnswer(node.questionId, next);
        };
        const onSetRowField = (rowIdx, fieldId, value) => {
          const next = items.map((row, i) => {
            if (i !== rowIdx) return row;
            const changed = { ...row, [fieldId]: value };
            // If editing guardian_email, clear confirm field
            if (fieldId === 'guardian_email') {
              changed['guardian_email_confirm'] = '';
            }
            return changed;
          });
          setAnswer(node.questionId, next);
        };
        return (
          <div className="space-y-4 text-left">
            <div className="text-sm text-gray-600">{node.label}{node.required ? <span className="text-red-500"> *</span> : null}</div>
            <div className={`space-y-4 ${prefersReducedMotion ? '' : 'transition-all'}`}>
              {items.map((row, rowIdx) => {
                const rowKey = row.__key || `${rowIdx}`;
                return (
                  <div key={rowKey} className="p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-gray-800">Guardian {rowIdx + 1}</div>
                      <button
                        type="button"
                        className={`text-sm underline disabled:text-gray-400`}
                        onClick={() => onRemove(rowIdx)}
                        disabled={items.length <= minItems}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(node.children || []).map((child) => {
                        if (child.type === 'text' || child.type === 'email' || child.type === 'textArea') {
                          const fieldValid = validateBySchema({ schema, question: child, answers: row, valIdx });
                          return (
                            <TextField
                              key={child.questionId}
                              id={`${child.questionId}__${rowIdx}`}
                              label={child.label}
                              type={child.type === 'email' ? 'email' : (child.type === 'textArea' ? 'textArea' : 'text')}
                              required={child.required}
                              value={row[child.questionId]}
                              validOverride={row[child.questionId] ? fieldValid : undefined}
                              onChange={(v) => onSetRowField(rowIdx, child.questionId, v)}
                            />
                          );
                        }
                        if (child.type === 'email_confirm') {
                          const fieldValid = validateBySchema({ schema, question: child, answers: row, valIdx });
                          const primaryValid = (() => {
                            const primaryChild = (current.children || []).find(ch => ch.questionId === 'guardian_email');
                            return primaryChild ? validateBySchema({ schema, question: primaryChild, answers: row, valIdx }) : !!row['guardian_email'];
                          })();
                          return (
                            <ConfirmEmailField
                              key={child.questionId}
                              id={`${child.questionId}__${rowIdx}`}
                              label={child.label}
                              required={child.required}
                              originalValue={row['guardian_email']}
                              value={row[child.questionId]}
                              validOverride={row[child.questionId] ? fieldValid : undefined}
                              onChange={(v) => onSetRowField(rowIdx, child.questionId, v)}
                              disabled={!primaryValid}
                            />
                          );
                        }
                        if (child.type === 'phone') {
                          return (
                            <PhoneNumberField
                              key={child.questionId}
                              label={child.label}
                              required={child.required}
                              rawValue={row[`${child.questionId}_raw`]}
                              onRawChange={(v) => onSetRowField(rowIdx, `${child.questionId}_raw`, v)}
                              onNormalizedChange={(e164) => onSetRowField(rowIdx, child.questionId, e164)}
                            />
                          );
                        }
                        if (child.type === 'radio') {
                          return (
                            <RadioGroup
                              key={child.questionId}
                              name={`${child.questionId}__${rowIdx}`}
                              label={child.label}
                              options={child.options}
                              required={child.required}
                              value={row[child.questionId]}
                              onChange={(v) => onSetRowField(rowIdx, child.questionId, v)}
                            />
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <button
                type="button"
                className={`px-3 py-2 rounded-md border border-primary text-primary hover:bg-primary/5 disabled:opacity-50`}
                onClick={onAdd}
                disabled={!canAdd}
              >
                + Add a parent / carer
              </button>
            </div>
          </div>
        );
      }
      case 'text': {
        const fieldValid = validateBySchema({ schema, question: node, answers: answersScope, valIdx });
        return (
          <TextField
            id={node.questionId}
            label={node.label}
            type="text"
            required={node.required}
            value={answersScope[node.questionId]}
            validOverride={answersScope[node.questionId] ? fieldValid : undefined}
            onChange={(v) => setAnswer(node.questionId, v)}
          />
        );
      }
      case 'textArea': {
        const fieldValid = validateBySchema({ schema, question: node, answers: answersScope, valIdx });
        return (
          <TextField
            id={node.questionId}
            label={node.label}
            type="textArea"
            required={node.required}
            value={answersScope[node.questionId]}
            validOverride={answersScope[node.questionId] ? fieldValid : undefined}
            onChange={(v) => setAnswer(node.questionId, v)}
          />
        );
      }
      case 'email':
        { const fieldValid = validateBySchema({ schema, question: node, answers: answersScope, valIdx });
          return (
            <TextField
              id={node.questionId}
              label={node.label}
              type="email"
              required={node.required}
              value={answersScope[node.questionId]}
              validOverride={answersScope[node.questionId] ? fieldValid : undefined}
              onChange={(v) => setAnswer(node.questionId, v)}
            />
          );
        }
      case 'email_confirm':
        { const fieldValid = validateBySchema({ schema, question: node, answers: answersScope, valIdx });
          return (
            <ConfirmEmailField
              id={node.questionId}
              label={node.label}
              required={node.required}
              originalValue={answersScope['ref_email']}
              value={answersScope[node.questionId]}
              validOverride={answersScope[node.questionId] ? fieldValid : undefined}
              onChange={(v) => setAnswer(node.questionId, v)}
            />
          );
        }
      case 'phone':
        return (
          <TextField
            id={node.questionId}
            label={node.label}
            required={node.required}
            value={answersScope[node.questionId]}
            onChange={(v) => setAnswer(node.questionId, v)}
          />
        );
      case 'phone_confirm': {
        // Hidden: confirm phone removed from UI; keep data consistent by auto-setting equal to primary
        return null;
      }
      case 'radio':
        // Use custom instruction label from schema meta if provided (e.g., for rating scales),
        // otherwise fall back to the default "Select one option" instruction
        return (
          <RadioGroup
            name={node.questionId}
            label={schema?.meta?.radioInstructionLabel || "Select one option"}
            options={node.options}
            required={node.required}
            value={answersScope[node.questionId]}
            onChange={(v) => setAnswer(node.questionId, v)}
            freeTextError={freeTextValidationMessage(node, answersScope[node.questionId], valIdx)}
            afterLabel={chrome === 'harbour' && node.questionId === current?.questionId
              ? <div className="harbour-save-status--inline">{renderHarbourSaveStatus()}</div>
              : null}
          />
        );
      case 'checkboxes':
        return (
          <CheckboxGroup
            name={node.questionId}
            label="Select as many options as apply"
            options={node.options}
            required={node.required}
            value={answersScope[node.questionId] || []}
            onChange={(arr) => setAnswer(node.questionId, arr)}
          />
        );
      case 'checkbox': {
        // Single "tick if this applies" item, used by the HFP v2 Sensory
        // Seeking/Avoidance modifiers. Options are an ordered pair:
        // [selected (e.g. ao_1, score 1), not-selected (e.g. ao_2, score 0)].
        // The question label is already shown as the step heading by QuestionCard,
        // so the checkbox itself carries the "selected" option label. We store the
        // selected/not-selected option VALUE (not a boolean) so the scoring engine
        // reads it the same way it reads the radio answers.
        const selectedOpt = node.options?.[0];
        const notSelectedOpt = node.options?.[1];
        const selectedVal = selectedOpt?.value ?? 'ao_1';
        const notSelectedVal = notSelectedOpt?.value ?? 'ao_2';
        const isChecked = answersScope[node.questionId] === selectedVal;
        return (
          <label className="flex items-start gap-3 cursor-pointer text-left">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={isChecked}
              onChange={(e) => setAnswer(node.questionId, e.target.checked ? selectedVal : notSelectedVal)}
            />
            <span className="text-sm text-gray-900">{selectedOpt?.label || 'Selected'}</span>
          </label>
        );
      }
      case 'date': {
        // Determine age band constraints from schema meta or context
        const ageBand = node?.meta?.ageBand || formContext?.pathway; // 'cyp' or 'adult'
        // For CYP DOB, narrow the selectable range by the selected school type:
        // primary 4-11, secondary 11-16. Falls back to the full CYP range (0-17)
        // until a school is chosen. The picker adds a 1-year buffer on the oldest end.
        // In flows that don't re-ask the school (post-account additional referral),
        // the phase can be supplied via formContext.schoolPhase instead.
        const schoolType = findSelectedSchoolType(answers) || formContext?.schoolPhase || null;
        const { minAgeYears, maxAgeYears } = dobAgeRange(ageBand, schoolType);
        return (
          <div className="text-left">
            {/* 
              UX note:
              - The question prompt (e.g. "What is their date of birth?") is rendered once by `QuestionCard`
                as the big heading for the step.
              - If we repeat `node.label` here, users see the prompt twice (heading + field label), which reads
                like a bug and adds cognitive noise.
              - For DOB specifically, we want a short, actionable instruction immediately above the 3 dropdowns.
            */}
            <div className="block text-sm font-medium text-gray-900 mb-2">
              Select options from the dropdown menus.
            </div>
            <DateOfBirthPicker
              value={answersScope[node.questionId] || ''}
              onChange={(v) => setAnswer(node.questionId, v)}
              className="w-full"
              minAgeYears={minAgeYears}
              maxAgeYears={maxAgeYears}
            />
          </div>
        );
      }
      case 'info':
        // Informational text - no input required, just display
        return (
          <InfoField question={node} />
        );
      case 'nomination': {
        // Modal-based participant nomination (e.g., Legal Guardian)
        // orgId and patientRecordId are passed for status checking (locked/pending)
        const nominationVal = answersScope[node.questionId];
        const hasNomination = Array.isArray(nominationVal)
          ? nominationVal.length > 0
          : !!(nominationVal && typeof nominationVal === 'object' && Object.keys(nominationVal).length > 0);
        const consentKey = `${node.questionId}__consent`;
        const consentLabel = node?.meta?.consentLabel
          || 'I confirm that the parent has consented to this referral, and they are expecting an email asking them to fill in the referral forms.';
        return (
          <>
            <NominationField
              question={node}
              value={nominationVal}
              onChange={(val) => setAnswer(node.questionId, val)}
              schema={schema}
              orgId={orgId}
              patientRecordId={patientRecordId}
            />
            {/* Consent gate: only required/shown once a nomination has been added,
                and only when the schema marks this nomination as needing consent. */}
            {node?.meta?.requireConsent && hasNomination && (
              <label className="mt-4 flex items-start gap-3 text-left rounded-lg border border-gray-200 bg-gray-50 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={answersScope[consentKey] === true}
                  onChange={(e) => setAnswer(consentKey, e.target.checked)}
                />
                <span className="text-sm text-gray-800">{consentLabel}</span>
              </label>
            )}
          </>
        );
      }
      case 'schoolSelect':
        // Searchable registered-school dropdown. Records school id/name/address
        // and tags the child primary/secondary (drives DOB bands + HFP/triage gating).
        return (
          <SchoolSelectorField
            question={node}
            value={answersScope[node.questionId]}
            onChange={(val) => setAnswer(node.questionId, val)}
          />
        );
      case 'milestoneAgeGroup':
        // Developmental milestone age inputs with "Not sure" checkboxes
        return (
          <MilestoneAgeGroupField
            question={node}
            value={answersScope[node.questionId]}
            onChange={(val) => setAnswer(node.questionId, val)}
          />
        );
      case 'physicalHealthInfoGroup':
        // Physical health measurement inputs (height/weight, head circ, pulse, BP).
        // We pass subjectDob from formContext so the year dropdowns can be constrained
        // to the CYP's lifetime (no dates before they were born).
        return (
          <PhysicalHealthInfoGroupField
            question={node}
            value={answersScope[node.questionId]}
            onChange={(val) => setAnswer(node.questionId, val)}
            subjectDob={formContext?.subjectDob}
          />
        );
      default:
        return null;
    }
  };

  const isChildValid = useCallback((child) => {
    return validateBySchema({ schema, question: child, answers, valIdx });
  }, [answers, schema, valIdx]);

  const isCurrentStepValid = useMemo(() => {
    if (!current) return false;
    // Info type is always valid - it's just informational text, no input required
    if (current.type === 'info') {
      return true;
    }
    // Nomination type - check if nomination data exists when required
    // Supports both single nomination (object) and multiple nominations (array)
    // with dynamic field name detection for flexibility across different schemas
    if (current.type === 'nomination') {
      const nominationValue = answers[current.questionId];

      // Consent gate: if a nomination has been added and the schema requires
      // consent, the confirmation must be ticked before continuing — even when
      // the nomination question itself is optional (TD-116).
      if (current.meta?.requireConsent) {
        const hasNom = Array.isArray(nominationValue)
          ? nominationValue.length > 0
          : !!(nominationValue && typeof nominationValue === 'object' && Object.keys(nominationValue).length > 0);
        if (hasNom && answers[`${current.questionId}__consent`] !== true) return false;
      }

      if (!current.required) return true;

      const children = current.children || [];
      // Dynamically find first_name and email field names from children schema
      const firstNameField = children.find(c => 
        c.questionId.includes('first_name') || 
        (c.type === 'text' && c.label?.toLowerCase().includes('first'))
      );
      const emailField = children.find(c => c.type === 'email' && !c.questionId.includes('confirm'));
      
      // Helper to validate a single nomination object has required fields
      const isValidNomination = (nomination) => {
        if (!nomination || typeof nomination !== 'object') return false;
        const hasFirstName = firstNameField ? !!nomination[firstNameField.questionId] : true;
        const hasEmail = emailField ? !!nomination[emailField.questionId] : true;
        return hasFirstName && hasEmail;
      };
      
      // Check for multiple mode (allowMultiple in meta)
      const allowMultiple = current.meta?.allowMultiple === true;

      let nominationOk;
      if (allowMultiple) {
        // For multiple mode, value should be an array with at least one valid nomination
        // Note: If required=true and allowMultiple=true, at least one nomination is needed
        if (!Array.isArray(nominationValue) || nominationValue.length === 0) nominationOk = false;
        // Verify at least the first nomination is valid (all should be valid if added through modal)
        else nominationOk = isValidNomination(nominationValue[0]);
      } else {
        // Single mode: validate the nomination object directly
        nominationOk = isValidNomination(nominationValue);
      }

      // Consent gate: when the nomination requires consent, the confirmation
      // checkbox must be ticked before the user can continue or review.
      if (nominationOk && current.meta?.requireConsent) {
        return answers[`${current.questionId}__consent`] === true;
      }
      return nominationOk;
    }
    // MilestoneAgeGroup type - check that each milestone has either an age, "notSure", or "notYetDoing" checked
    if (current.type === 'milestoneAgeGroup') {
      const milestoneValue = answers[current.questionId];
      if (!current.required) return true;
      // For required milestone groups, check that all milestones have been addressed
      if (!milestoneValue || typeof milestoneValue !== 'object') return false;
      const milestones = current.milestones || [];
      // All milestones must have either a valid age, notSure=true, or notYetDoing=true
      return milestones.every((m) => {
        const entry = milestoneValue[m.milestoneId];
        if (!entry) return false;
        // Valid if age is a finite number OR notSure is true OR notYetDoing is true
        return isFiniteNumberLike(entry.age) || entry.notSure === true || entry.notYetDoing === true;
      });
    }
    // PhysicalHealthInfoGroup type - the measurement questions are optional, but a
    // value outside the plausible range (its message shows on the field) holds the
    // step until it is corrected or cleared. If one were ever made required,
    // isPhysicalHealthValid would gate it too.
    if (current.type === 'physicalHealthInfoGroup') {
      if (hasMeasurementProblem(current.measurementType, answers[current.questionId])) return false;
      if (!current.required) return true;
      return isPhysicalHealthValid(current, answers[current.questionId], true);
    }
    // SchoolSelect type - require a school to be chosen when the question is required.
    if (current.type === 'schoolSelect') {
      if (!current.required) return true;
      return !!answers[current.questionId]?.schoolId;
    }
    if (current.type === 'group') {
      return (current.children || []).every(isChildValid);
    }
    if (current.type === 'repeatable_group') {
      const items = Array.isArray(answers[current.questionId]) ? answers[current.questionId] : [];
      const minItems = Number(current.minItems || 0);
      if (current.required && items.length < minItems) return false;
      // If no items required and none present, treat as valid
      if (items.length === 0) return !current.required;
      const children = current.children || [];
      return items.every((row) => {
        return children.every((ch) => {
          // apply child required/validators using row as answers
          return validateBySchema({ schema, question: ch, answers: row, valIdx });
        });
      });
    }
    return validateBySchema({ schema, question: current, answers, valIdx });
  }, [current, answers, isChildValid, schema, valIdx, isFiniteNumberLike, isPhysicalHealthValid]);

  /**
   * sectionProgress - Per-section progress calculation for footer progress bars
   * 
   * PREFILL-PROGRESS INTERACTION:
   * ─────────────────────────────
   * When forms have prefilled data (from previous submissions, other respondents, etc.),
   * we must NOT count prefilled answers toward progress until the user explicitly confirms
   * them by advancing through the form. This ensures accurate progress representation:
   * 
   *   1. Backend returns `prefillData` with suggested answers (separate from `answers`)
   *   2. Frontend merges prefillData into form state and tracks which IDs are "unconfirmed"
   *      in the `prefillSources` Set
   *   3. Progress calculation checks `prefillSources` to exclude unconfirmed prefills
   *   4. When user clicks Next on a step, `onConfirmPrefills` removes those IDs from
   *      `prefillSources`, allowing them to count toward progress
   * 
   * DEBUGGING: Pass debug={true} prop to see console.table output of progress calculation
   */
  const sectionProgress = useMemo(() => {
    const secs = visibleSections || [];
    const progressValues = [];
    const debugRows = [];

    // Convert prefillSources Set to a lookup for performance
    const prefillSet = prefillSources instanceof Set ? prefillSources : new Set();

    // ─────────────────────────────────────────────────────────────────────────────
    // SHARED HELPER FUNCTIONS
    // These are reused by both hasAnyValue (optional) and isStepAnswered (required)
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Check if a radio answer is valid (has selection, and free text if required)
     */
    const isRadioValid = (q, v) => {
      const selectedId = v?.optionId ?? v;
      if (!selectedId) return false;
      const selectedOpt = (q.options || []).find(o => o.optionId === selectedId);
      if (!selectedOpt) return false;
      // Require free text if option has allowsFreeText and NOT freeTextOptional
      if (selectedOpt.allowsFreeText && selectedOpt.freeTextOptional !== true) {
        if (!(v?.otherText && String(v.otherText).trim())) return false;
      }
      // Validators scoped to the selected option (the NHS number on "Yes, I
      // know it", TDI-510) decide too, as validateBySchema does.
      for (const vs of (q.validators || [])) {
        if (!applyValidator(valIdx, vs, { [q.questionId]: v }, q.questionId)) return false;
      }
      return true;
    };

    /**
     * Check if checkbox answer is valid (has selections, free text filled where required)
     */
    const isCheckboxValid = (q, v) => {
      if (!Array.isArray(v) || v.length === 0) return false;
      // Verify free text is filled for any selected option that requires it
      for (const opt of (q.options || []).filter(o => o.allowsFreeText)) {
        const selected = v.find(x => (x?.optionId ?? x) === opt.optionId);
        if (selected && opt.freeTextOptional !== true) {
          if (!selected.otherText || !String(selected.otherText).trim()) return false;
        }
      }
      return true;
    };

    /**
     * Check if milestoneAgeGroup is valid (all/some milestones addressed)
     * @param requireAll - true for required questions (all milestones), false for optional (any milestone)
     *
     * A milestone is "addressed" if it has:
     *   - A valid numeric age, OR
     *   - "Not sure" checked (notSure: true), OR
     *   - "Not yet doing" checked (notYetDoing: true)
     */
    const isMilestoneValid = (q, v, requireAll) => {
      if (!v || typeof v !== 'object') return false;
      const milestones = q.milestones || [];
      const checkFn = (m) => {
        const entry = v[m.milestoneId];
        return entry && (isFiniteNumberLike(entry.age) || entry.notSure === true || entry.notYetDoing === true);
      };
      return requireAll ? milestones.every(checkFn) : milestones.some(checkFn);
    };

    /**
     * Find essential nomination fields dynamically (matches NominationField component logic)
     * Caches result on the question object to avoid repeated lookups
     */
    const getNominationFieldKeys = (q) => {
      // Use cached result if available (avoids repeated array searches)
      if (q._nominationFieldKeys) return q._nominationFieldKeys;
      
      const children = q.children || [];
      const firstNameChild = children.find(c => 
        c.questionId?.includes('first_name') || 
        (c.type === 'text' && c.label?.toLowerCase().includes('first'))
      );
      const emailChild = children.find(c => 
        c.type === 'email' && !c.questionId?.includes('confirm')
      );
      
      q._nominationFieldKeys = {
        firstNameKey: firstNameChild?.questionId,
        emailKey: emailChild?.questionId
      };
      return q._nominationFieldKeys;
    };

    /**
     * Check if nomination has essential fields filled
     */
    const isNominationValid = (q, v) => {
      if (!v || typeof v !== 'object') return false;
      const { firstNameKey, emailKey } = getNominationFieldKeys(q);
      const hasFirstName = firstNameKey && !!(v[firstNameKey] && String(v[firstNameKey]).trim());
      const hasEmail = emailKey && !!(v[emailKey] && String(v[emailKey]).trim());
      return hasFirstName && hasEmail;
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // PROGRESS CALCULATION FUNCTIONS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * hasAnyValue - Check if OPTIONAL question has user-provided value
     * 
     * For optional questions, we check if user provided ANY value (not necessarily complete).
     * Prefill check ensures prefilled values don't count until user confirms via Next.
     */
    const hasAnyValue = (q) => {
      // Prefilled answers don't count until confirmed
      if (prefillSet.has(q.questionId)) return false;
      
      const v = answers?.[q.questionId];
      if (v == null) return false;

      switch (q.type) {
        case 'radio':
          return isRadioValid(q, v);
        case 'checkbox':
          // Single tick item (HFP modifier): any stored option value counts.
          return v != null && String(v) !== '';
        case 'checkboxes':
          return isCheckboxValid(q, v);
        case 'text':
        case 'textArea':
        case 'email':
        case 'phone':
        case 'date':
          return String(v || '').trim() !== '';
        case 'group':
          // Recursively check if ANY child has value (prefill checked per-child)
          return (q.children || []).some(ch => hasAnyValue(ch));
        case 'repeatable_group':
          return Array.isArray(v) && v.length > 0;
        case 'milestoneAgeGroup':
          return isMilestoneValid(q, v, false); // At least one milestone addressed
        case 'physicalHealthInfoGroup':
          return isPhysicalHealthValid(q, v, false); // Any primary field has a value
        case 'nomination':
          return isNominationValid(q, v);
        default:
          return false;
      }
    };

    /**
     * isStepAnswered - Check if REQUIRED question is fully answered
     * 
     * For groups, also checks if any children are prefilled (backend may prefill
     * individual child fields like school_addr_street rather than parent group ID).
     */
    const isStepAnswered = (q) => {
      // Prefilled answers don't count until confirmed
      if (prefillSet.has(q.questionId)) return false;

      const v = answers?.[q.questionId];

      switch (q.type) {
        case 'group': {
          const children = q.children || [];
          if (!children.length) return false;
          const visibleChildren = children.filter(ch => 
            isQuestionVisible(answers, ch.visibleWhen, formContext)
          );
          // If ANY visible child is prefilled, group doesn't count as answered
          if (visibleChildren.some(ch => prefillSet.has(ch.questionId))) return false;
          // All visible children must pass validation
          return visibleChildren.every(ch => 
            validateBySchema({ schema, question: ch, answers, valIdx })
          );
        }
        
        case 'radio':
          return isRadioValid(q, v);

        case 'checkbox':
          // Single tick item (HFP modifier): answered once an option value is stored.
          return v != null && String(v) !== '';

        case 'checkboxes':
          return isCheckboxValid(q, v);

        case 'repeatable_group': {
          const items = Array.isArray(v) ? v : [];
          const minItems = Number(q.minItems || 0);
          if (items.length < minItems || items.length === 0) return false;
          const children = q.children || [];
          return items.every(row => 
            children.every(ch => validateBySchema({ schema, question: ch, answers: row, valIdx }))
          );
        }
        
        case 'milestoneAgeGroup':
          return isMilestoneValid(q, v, true); // ALL milestones must be addressed
        
        case 'physicalHealthInfoGroup':
          return isPhysicalHealthValid(q, v, true); // ALL primary fields + dates filled
        
        case 'nomination':
          return isNominationValid(q, v);
        
        default:
          return validateBySchema({ schema, question: q, answers, valIdx });
      }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // CALCULATE PROGRESS PER SECTION
    // ─────────────────────────────────────────────────────────────────────────────

    for (const sec of secs) {
      // Get actionable questions (visible, conditional gate satisfied, not info-only).
      // The conditionalOn gate keeps hidden HFP modifiers (e.g. Sensory Seeking/
      // Avoidance) out of the denominator so the section can still reach 100%.
      const actionableQs = (sec.questions || [])
        .filter(q => isQuestionVisible(answers, q.visibleWhen, formContext))
        .filter(q => isConditionalMet(answers, q))
        .filter(q => q.type !== 'info');
      
      const total = actionableQs.length;
      if (total === 0) {
        progressValues.push(0);
        continue;
      }

      // Count answered questions
      let answered = 0;
      for (const q of actionableQs) {
        if (q.required === true) {
          // Required: must be fully answered
          if (isStepAnswered(q)) answered++;
        } else {
          // Optional: counts if has value OR user explicitly passed it
          if (hasAnyValue(q) || consideredOptionalStepIds.has(q.questionId)) answered++;
        }
      }

      const pct = Math.round((answered / total) * 100);
      progressValues.push(pct);
      
      if (debug) {
        debugRows.push({
          section: sec.title,
          totalQuestions: total,
          answeredQuestions: answered,
          percent: pct
        });
      }
    }

    if (debug) {
      try { console.table(debugRows); } catch (_) {}
    }

    return progressValues;
  }, [schema, answers, valIdx, formContext, visibleSections, debug, consideredOptionalStepIds, prefillSources, isFiniteNumberLike, isPhysicalHealthValid]);

  // Keep ref updated with latest sectionProgress for use in callbacks
  // This allows maybeSaveCurrentStep to access current progress without re-subscribing
  useEffect(() => {
    sectionProgressRef.current = sectionProgress;
  }, [sectionProgress]);

  /**
   * effectiveSectionProgress - What we actually display in the footer
   * 
   * Priority order (first valid wins):
   *   1. displayedSectionProgress - stable, only updates on forward navigation
   *   2. serverSectionProgress - from Firestore (resume scenario)
   *   3. sectionProgress - live calculation (fallback for first question)
   * 
   * This ensures progress bar is stable while user is typing/selecting,
   * only updating when they click Next (forward navigation).
   */
  const effectiveSectionProgress = useMemo(() => {
    const localCount = (visibleSections || []).length;
    
    // Prefer displayed progress if available and correct length
    if (Array.isArray(displayedSectionProgress) && displayedSectionProgress.length === localCount) {
      return displayedSectionProgress;
    }
    
    // Fallback to server progress on initial load / resume
    if (Array.isArray(serverSectionProgress) && serverSectionProgress.length === localCount) {
      return serverSectionProgress;
    }
    
    // Final fallback to live calculation (first question before any forward navigation)
    return sectionProgress;
  }, [displayedSectionProgress, serverSectionProgress, sectionProgress, visibleSections]);

  const partIndex = useMemo(() => {
    if (!currentSection) return 1;
    // Index among visible sections only
    const idx2 = visibleSections.findIndex((s) => s === currentSection || (s.questions || []).some(q => current?.questionId === q.questionId));
    return idx2 >= 0 ? idx2 + 1 : 1;
  }, [visibleSections, currentSection, current?.questionId]);
  const partTotal = useMemo(() => visibleSections.length || 1, [visibleSections]);

  // Minutes remaining text with singular/zero rules handled below in timeRemainingText

  const timeRemainingText = useMemo(() => {
    const total = visibleSteps.length;

    const isRadioAnswered = (q) => {
      const v = answers?.[q.questionId];
      const selectedId = v?.optionId ?? v;
      if (!selectedId) return false;
      const opt = (q.options || []).find(o => o.optionId === selectedId);
      if (!opt) return false;
      // If the selected option allows free text and doesn't have freeTextOptional: true,
      // require otherText to be filled in for the answer to be considered complete.
      if (opt.allowsFreeText && opt.freeTextOptional !== true) {
        if (!(v?.otherText && String(v.otherText).trim() !== '')) return false;
      }
      for (const vs of (q.validators || [])) {
        if (!applyValidator(valIdx, vs, { [q.questionId]: v }, q.questionId)) return false;
      }
      return true;
    };
    const isGroupAnswered = (q) => {
      const children = q.children || [];
      if (!children.length) return false;
      return children
        .filter(ch => isQuestionVisible(answers, ch.visibleWhen, formContext))
        .every((ch) => validateBySchema({ schema, question: ch, answers, valIdx }));
    };
      const isAddressAnswered = (q) => {
        const v = answers?.[q.questionId];
        if (!v) return false;
        const hasStreet = !!String(v.addr_street || '').trim();
        const hasCity = !!String(v.addr_city || '').trim();
        const hasPostcode = isLikelyUkPostcode(v.addr_postcode || '');
        return hasStreet && hasCity && hasPostcode;
      };
    const isStepAnswered = (q) => {
      switch (q.type) {
        case 'group': return isGroupAnswered(q);
        case 'radio': return isRadioAnswered(q);
        case 'address': return isAddressAnswered(q);
        case 'repeatable_group': {
          const items = Array.isArray(answers?.[q.questionId]) ? answers[q.questionId] : [];
          const minItems = Number(q.minItems || 0);
          if (items.length < (minItems || 0)) return false;
          if (items.length === 0) return false;
          const children = q.children || [];
          return items.every((row) => children.every((ch) => validateBySchema({ schema, question: ch, answers: row, valIdx })));
        }
        case 'milestoneAgeGroup': {
          // All milestones must have either an age, notSure, or notYetDoing checked
          const v = answers?.[q.questionId];
          if (!v || typeof v !== 'object') return false;
          const milestones = q.milestones || [];
          return milestones.every((m) => {
            const entry = v[m.milestoneId];
            return entry && (isFiniteNumberLike(entry.age) || entry.notSure === true || entry.notYetDoing === true);
          });
        }
        case 'physicalHealthInfoGroup': {
          // Physical health measurements — delegate to the shared validator
          const v = answers?.[q.questionId];
          return isPhysicalHealthValid(q, v, true);
        }
        default: return validateBySchema({ schema, question: q, answers, valIdx });
      }
    };

    const answered = visibleSteps.reduce((acc, s) => acc + (isStepAnswered(s) ? 1 : 0), 0);
    const remaining = Math.max(total - answered, 0);
    if (remaining === 0) return 'All questions answered';
    const perQuestionSeconds = 30;
    const seconds = remaining * perQuestionSeconds;
    const minutes = Math.round(seconds / 60);
    if (minutes < 1) return 'Less than 1 minute remaining';
    if (minutes === 1) return 'About 1 minute remaining';
    return `About ${minutes} minutes remaining`;
  }, [visibleSteps, answers, schema, valIdx, formContext, isFiniteNumberLike, isPhysicalHealthValid]);

  if (!schema) return null;

  const isHarbourChrome = chrome === 'harbour';
  const rootClassName = isHarbourChrome
    ? `harbour harbour-form harbour-form--${chromeAudience} harbour-form--tone-${chromeTone} rms-form-root min-h-screen flex flex-col`
    : 'rms-form-root min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col';

  const submitFromReview = async () => {
    if (reviewSubmitting) return;
    setReviewSubmitting(true);
    setReviewSubmitError(null);
    try {
      await onComplete?.(answers);
    } catch (e) {
      // Submit failed AFTER answers were saved as a draft. Do NOT navigate
      // away or treat as done — surface the failure so the user can retry;
      // otherwise the form is stranded at 100% draft and reads as unfilled.
      // One failure is permanent, not retryable: the respondent has
      // confirmed (possibly in another tab), so the answers are
      // locked (TDI-415) — say that instead of inviting a retry.
      setReviewSubmitError(
        String(e?.code || '').includes('failed-precondition')
          ? 'These answers have been confirmed and submitted, so they can no longer be changed.'
          : "We couldn't submit your form just now. Your answers are saved — please check your connection and try again."
      );
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (mode === 'review' && isHarbourChrome) {
    const isFamily = chromeAudience === 'family';
    const editStep = reviewEditIndex != null ? visibleSteps[reviewEditIndex] : null;
    const sectionTitles = [];
    visibleSteps.forEach((st) => {
      const t = st.sectionTitle || '';
      if (!sectionTitles.includes(t)) sectionTitles.push(t);
    });
    const editPart = editStep
      ? (sectionTitles.length > 1
        ? `Part ${sectionTitles.indexOf(editStep.sectionTitle || '') + 1} of ${sectionTitles.length}${editStep.sectionTitle ? `: ${editStep.sectionTitle}` : ''}`
        : (editStep.sectionTitle || ''))
      : '';
    const submitLabel = isEditingSubmittedForm ? 'Save changes' : (isFamily ? 'Finish' : reviewNextLabel);
    return (
      <div className={rootClassName} ref={rootRef}>
        <div ref={headerRef} className="sticky top-0 z-30 harbour-form-header">
          <div className="w-full px-6 md:px-10 pt-5 pb-2 flex items-center">
            {brandBackTo && (
              <HarbourTextLink leadingIcon="arrow-left" onClick={handleNavigateOverview}>
                {chromeBackLabel}
              </HarbourTextLink>
            )}
          </div>
        </div>
        <div
          ref={liveRegionRef}
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}
        />
        <div className={`harbour-review-layout${editStep ? ' harbour-review-layout--editing' : ''}`}>
          <div className="harbour-review-card">
            {locked && (
              <HarbourNotice kind="info" title="Your answers are submitted" className="harbour-review__notice">
                {lockedNotice}
              </HarbourNotice>
            )}
            {!locked && isEditingSubmittedForm && (
              <HarbourNotice kind="warning" title="You have changes to save" className="harbour-review__notice">
                Your changes are not saved until you select Save changes.{' '}
                <button type="button" className="harbour-review__link" onClick={discardChanges}>Discard changes</button>
              </HarbourNotice>
            )}
            <HarbourReviewList
              steps={visibleSteps}
              answers={answers}
              eyebrow={isFamily ? (schema?.title || null) : null}
              tone={chromeTone}
              titleRef={headingRef}
              notice={isFamily
                ? 'Select any answer to change it'
                : 'Select any answer to change it, or submit when you are ready.'}
              readOnly={locked}
              compact={Boolean(editStep)}
              selectedIndex={reviewEditIndex}
              updatedIndex={reviewUpdatedIndex}
              onSelect={openReviewEdit}
            />
          </div>
          {editStep && (
            <section className="harbour-review-edit rms-question-card" aria-label="Change your answer">
              <div className="harbour-review-edit__head">
                <p className="harbour-review-edit__part">{editPart}</p>
                <p className="harbour-review-edit__count">{`Question ${reviewEditIndex + 1} of ${visibleSteps.length}`}</p>
                <HarbourIconButton kind="ghost" icon="close" label="Close this question" onClick={closeReviewEdit} />
              </div>
              {questionPreamble ? <p className="rms-question-preamble">{questionPreamble}</p> : null}
              <h2>{editStep.label}</h2>
              {editStep.description ? <p className="harbour-body-16">{editStep.description}</p> : null}
              {renderStepNode(editStep)}
              <div className="harbour-review-edit__status">{renderHarbourSaveStatus()}</div>
              {saveError && (
                // A refused save must reach the person here too, not only on
                // the question pages: this panel is where review changes are
                // made (TDI-754).
                <HarbourNotice kind="error" title={SAVE_FAILED_TITLE}>
                  <p>{saveError.detail}</p>
                  {!saveError.refused && (
                    <button type="button" className="underline" onClick={retryLastSave}>Try again</button>
                  )}
                </HarbourNotice>
              )}
            </section>
          )}
        </div>
        <div className="harbour-review-bar">
          <p className="harbour-review-bar__note">
            {!locked && !isFamily && !isEditingSubmittedForm ? reviewBarNote : ''}
          </p>
          {reviewSubmitError && (
            <p role="alert" className="harbour-review-bar__error">{reviewSubmitError}</p>
          )}
          {locked ? (
            <HarbourButton kind={isFamily ? 'family' : 'primary'} onClick={handleNavigateOverview}>
              {chromeBackLabel}
            </HarbourButton>
          ) : (
            <HarbourButton
              kind={isFamily ? 'family' : 'primary'}
              trailingIcon={isFamily ? undefined : 'arrow-right'}
              busy={reviewSubmitting}
              onClick={async () => {
                if (editStep) closeReviewEdit();
                await submitFromReview();
              }}
            >
              {submitLabel}
            </HarbourButton>
          )}
        </div>
      </div>
    );
  }

  if (mode === 'review') {
    return (
      <div className={rootClassName} ref={rootRef}>
      {isHarbourChrome ? (
        // Harbour chrome: no white header band — a bare canvas with the
        // floating back link, and "Review your answers" as the page heading
        // over the centred column (TDI-53 review frames).
        <div ref={headerRef} className="sticky top-0 z-30 harbour-form-header">
          <div className="w-full px-6 md:px-10 pt-5 pb-2">
            {brandBackTo && (
              <HarbourTextLink leadingIcon="arrow-left" onClick={handleNavigateOverview}>
                {chromeBackLabel}
              </HarbourTextLink>
            )}
            <div className="w-full md:w-1/2 mx-auto pt-3">
              <h1 ref={headingRef} tabIndex={-1} className="harbour-heading-24">Review your answers</h1>
            </div>
          </div>
        </div>
      ) : (
        <HeaderContainer
          headerRef={headerRef}
          breadcrumbItems={
            (!showBrand && brandBackTo)
              ? [
                  { label: brandBackLabel, onClick: handleNavigateOverview, icon: UserIcon },
                ]
              : null
          }
        >
          {showBrand ? <BrandRow logoSrc={providerLogo} alt="LCS logo" /> : null}
          <FormHeader
            formTitle={schema?.title || schema?.formKey || ''}
            headingRef={headingRef}
            headingOverride="Review your answers"
          />
        </HeaderContainer>
      )}
        {/* Live region (review) */}
        <div
          ref={liveRegionRef}
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}
        />        
      <div className="flex-grow w-full px-6 md:px-10 text-left" style={{ paddingBottom: 'var(--rms-footer-height)' }}>
        {/* Top gap using CSS variable populated by useLayoutMetrics */}
        <div style={{ height: 'var(--rms-question-top-gap)' }} />
        
        {/* Confirmed answers are locked (TDI-415): say so plainly, once. */}
        {locked && (
          <div className="w-full md:w-1/2 mx-auto mb-4">
            <div
              role="status"
              className="px-4 py-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
            >
              {lockedNotice}
            </div>
          </div>
        )}

        {/* Unsaved Changes Banner - shows when editing a submitted form */}
        {!locked && isEditingSubmittedForm && (
          <div className="w-full md:w-1/2 mx-auto mb-4">
            <div className="px-4 py-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    You have unsaved changes
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Your edits won&apos;t be saved until you click &quot;Save Changes&quot; below.
                  </p>
                </div>
              </div>
              
              {/* Quick action buttons in banner */}
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={discardChanges}
                  className="px-3 py-1.5 text-sm text-amber-700 hover:text-amber-800 hover:bg-amber-100 rounded-md transition-colors"
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        )}
        
        <ReviewList
          steps={visibleSteps}
          answers={answers}
          readOnly={locked}
          onEdit={locked ? undefined : (i) => beginEditReturn(i)}
        />
          <div className="w-full md:w-1/2 mx-auto pb-6 rms-review-submit">
            {reviewSubmitError && (
              <div
                role="alert"
                className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {reviewSubmitError}
              </div>
            )}
            {locked ? (
              <SubmitBar
                onNext={handleNavigateOverview}
                nextLabel="Back to your forms"
                hideBack
              />
            ) : (
            <SubmitBar
              onBack={onBack}
              onNext={async () => {
                if (reviewSubmitting) return;
                setReviewSubmitting(true);
                setReviewSubmitError(null);
                try {
                  await onComplete?.(answers);
                } catch (e) {
                  // Submit failed AFTER answers were saved as a draft. Do NOT navigate
                  // away or treat as done — surface the failure so the user can retry;
                  // otherwise the form is stranded at 100% draft and reads as unfilled.
                  // One failure is permanent, not retryable: the respondent has
                  // confirmed (possibly in another tab), so the answers are
                  // locked (TDI-415) — say that instead of inviting a retry.
                  setReviewSubmitError(
                    String(e?.code || '').includes('failed-precondition')
                      ? 'These answers have been confirmed and submitted, so they can no longer be changed.'
                      : "We couldn't submit your form just now. Your answers are saved — please check your connection and try again."
                  );
                } finally {
                  setReviewSubmitting(false);
                }
              }}
              nextLabel={isEditingSubmittedForm ? 'Save Changes' : reviewNextLabel}
              hideBack={false}
              disabled={reviewSubmitting}
              disableBack={reviewSubmitting}
              loading={reviewSubmitting}
              loadingLabel={isEditingSubmittedForm ? 'Saving...' : reviewLoadingLabel}
            />
            )}
          </div>
        </div>
        {!isHarbourChrome && (
          <FooterProgress
            ref={footerRef}
            timeRemainingText={timeRemainingText}
            sectionProgress={effectiveSectionProgress}
            prefersReducedMotion={prefersReducedMotion}
          />
        )}

      </div>
    );
  }

  // Harbour progress facts: "FormTitle - Part X of Y: Section" plus
  // "Question N of M", with one segment per part (counts, never percentages —
  // the strip is decorative reinforcement beside the words).
  const harbourSectionSteps = isHarbourChrome
    ? visibleSteps.filter((s) => s.sectionTitle === currentSection?.title)
    : [];
  const harbourIdxWithinSection = isHarbourChrome
    ? Math.max(0, harbourSectionSteps.findIndex((s) => s.questionId === current?.questionId))
    : 0;
  // Segment widths proportional to questions per part (the design's weighted
  // progress bar), in section order.
  // A plain computation, not a hook: this runs after the review early return,
  // and a hook here changes the hook count between question and review mode
  // ("Rendered fewer hooks than expected"), crashing the form on Review.
  const harbourPartWeights = (() => {
    if (!isHarbourChrome) return null;
    const order = [];
    const counts = {};
    for (const step of visibleSteps) {
      const key = step.sectionTitle || '';
      if (!(key in counts)) { counts[key] = 0; order.push(key); }
      counts[key] += 1;
    }
    return order.map((key) => counts[key]);
  })();
  // The sticky bottom bar mirrors the active card's navigation (the design's
  // mobile Task layout: Back and the single main action always in view).
  const harbourIsLast = currentIndex === visibleSteps.length - 1;
  const harbourNextLabel = editReturnToReview
    ? 'Go back to Review'
    : (harbourIsLast ? 'Review' : 'Next question');
  // Harbour pages draw Back on the first question too: it returns to the
  // forms homepage (3192:117969, 3453:12974).
  const harbourHideBack = editReturnToReview;
  const harbourBack = currentIndex === 0 ? handleNavigateOverview : onBack;
  // The question on screen counts towards its part, so question 1 already
  // shows a sliver of fill (frame 3192:117969).
  const harbourCurrentFraction = harbourSectionSteps.length
    ? (harbourIdxWithinSection + 1) / harbourSectionSteps.length
    : 0;

  return (
    <div className={rootClassName} ref={rootRef}>
      {isHarbourChrome ? (
        // Harbour chrome (TDI-53 question frames). Desktop: bare canvas,
        // floating back link top-left, centred progress strip. Mobile
        // (under 1100px): the Task app bar — Close + "Question N of M", the
        // Where line, then the weighted progress bar, all sticky.
        <div ref={headerRef} className="sticky top-0 z-30 harbour-form-header">
          <div className="harbour-form-header__desktop w-full px-6 md:px-10 pt-5 pb-2">
            <div className="flex items-start">
              {editReturnToReview ? (
                <HarbourTextLink leadingIcon="arrow-left" onClick={closeEditReturn}>
                  Back to your answers
                </HarbourTextLink>
              ) : (brandBackTo && (
                <HarbourTextLink leadingIcon="arrow-left" onClick={handleNavigateOverview}>
                  {chromeBackLabel}
                </HarbourTextLink>
              ))}
              <span className="ml-auto">
                <HarbourIconButton
                  kind="outlined"
                  icon="close"
                  label={editReturnToReview ? 'Close this question' : 'Close and go back to all forms'}
                  onClick={editReturnToReview ? closeEditReturn : handleNavigateOverview}
                />
              </span>
            </div>
            <div className="w-full md:w-1/2 mx-auto pt-2">
              <div className="harbour-form-progress">
                <div className="harbour-form-progress__row">
                  <span className="harbour-form-progress__title" ref={headingRef} tabIndex={-1}>
                    {`${schema?.title || schema?.formKey || ''}${currentSection?.title ? ` · Part ${partIndex} of ${partTotal}: ${currentSection.title}` : ''}`}
                  </span>
                  <span className="harbour-form-progress__count">
                    {`Question ${Math.min(currentIndex + 1, visibleSteps.length)} of ${visibleSteps.length}`}
                  </span>
                </div>
                <HarbourProgressSegments
                  tone={chromeTone}
                  total={partTotal}
                  doneCount={Math.max(0, partIndex - 1)}
                  currentFraction={harbourCurrentFraction}
                  weights={harbourPartWeights}
                />
              </div>
            </div>
          </div>
          <div className="harbour-task-bar">
            <div className="harbour-task-bar__row">
              <button type="button" className="harbour-task-bar__close" onClick={editReturnToReview ? closeEditReturn : handleNavigateOverview}>
                <HarbourIcon name="close" size={24} />
                Close
              </button>
              <span className="harbour-task-bar__counter">
                {`Question ${Math.min(currentIndex + 1, visibleSteps.length)} of ${visibleSteps.length}`}
              </span>
            </div>
            <p className={`harbour-task-bar__where harbour-task-bar__where--${chromeTone}`}>
              {`${schema?.title || schema?.formKey || ''}${currentSection?.title ? ` · Part ${partIndex} of ${partTotal}: ${currentSection.title}` : ''}`}
            </p>
            <div className="harbour-task-bar__progress">
              <HarbourProgressSegments
                tone={chromeTone}
                total={partTotal}
                doneCount={Math.max(0, partIndex - 1)}
                currentFraction={harbourCurrentFraction}
                weights={harbourPartWeights}
              />
            </div>
          </div>
        </div>
      ) : (
        <HeaderContainer
          headerRef={headerRef}
          breadcrumbItems={
            (!showBrand && brandBackTo)
              ? [
                  { label: brandBackLabel, onClick: handleNavigateOverview, icon: UserIcon },
                ]
              : null
          }
        >
          {showBrand ? <BrandRow logoSrc={providerLogo} alt="LCS logo" backTo={brandBackTo} /> : null}
          <FormHeader
            formTitle={schema?.title || schema?.formKey || ''}
            partIndex={partIndex}
            partTotal={partTotal}
            sectionTitle={currentSection?.title || ''}
            rightSlot={
              <div className="flex flex-col items-end">
                <SavingIndicator saving={saving} />
                {/* Reassurance that there's no pressure to finish in one sitting
                    (UAT feedback): make the autosave explicit and tell people they
                    can leave and resume. Sits beside the Saving/Saved indicator so
                    the two read together. */}
                <span className="mt-0.5 max-w-[16rem] text-xs text-gray-500">
                  Your answers save as you go — you can come back and finish later.
                </span>
              </div>
            }
            headingRef={headingRef}
          />
        </HeaderContainer>
      )}
      {/* Live region for announcing the active question; visually hidden */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}
      />
      
      {/* Edit Mode Indicator - subtle notice when editing a submitted form */}
      {wasSubmitted && (
        <div className="w-full px-6 md:px-10">
          <div className="w-full md:w-1/2 mx-auto">
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-amber-700 bg-amber-50/80 border border-amber-200/50 rounded-lg mb-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span>Editing submitted form &bull; Return to Review to save changes</span>
            </div>
          </div>
        </div>
      )}
      
      <div
        className="flex-grow w-full px-6 md:px-10 text-left"
        // Wheel/trackpad scrolling should work with the pointer anywhere over
        // the question row, not only over the centred card column (UAT
        // feedback). Forward wheel events from the full-width row to the
        // active card's internal scroll container.
        onWheel={(e) => forwardWheelToActiveCard(e.currentTarget, e)}
      >
        <div className="w-full md:w-1/2 mx-auto">
          <QuestionViewport viewportRef={scrollContainerRef}>
            <QuestionTrack
              currentIndex={currentIndex}
              prefersReducedMotion={prefersReducedMotion}
              onEnter={() => {}}
              onExit={() => {}}
              renderCard={(i) => {
                const step = visibleSteps[i];
                if (!step) return null;
                const isLast = i === (visibleSteps.length - 1);
                const nextLabelBase = editReturnToReview
                  ? 'Go back to Review'
                  : (isLast
                    ? (isHarbourChrome ? 'Review your answers' : 'Review')
                    : (isHarbourChrome ? 'Next question' : 'Next'));
                const isAddressStep =
                  !!step &&
                  (
                    step.questionId === 'subject_address' ||
                    (Array.isArray(step.children) && step.children.some((ch) => ch?.questionId === 'addr_postcode'))
                  );
                // Email step requires async validation to check if email is already registered
                const isEmailStep = !!step && step.questionId === 'ref_email_group';
                
                // Determine appropriate loading label based on step type
                let nextLabel = nextLabelBase;
                if (navLocked) {
                  if (isAddressStep) {
                    nextLabel = 'Checking postcode...';
                  } else if (isEmailStep) {
                    nextLabel = 'Loading...';
                  }
                }
                
                // Show loading spinner for both address and email steps when navLocked
                const showLoading = navLocked && (isAddressStep || isEmailStep);
                
                // Determine appropriate loading label based on step type
                let loadingLabel;
                if (isAddressStep) {
                  loadingLabel = 'Checking postcode...';
                } else if (isEmailStep) {
                  loadingLabel = 'Checking email...';
                }
                
                return (
                  <QuestionCard
                    stepKey={step.questionId}
                    onBack={isHarbourChrome ? harbourBack : onBack}
                    onNext={editReturnToReview ? onReturnToReviewGuarded : onNext}
                    nextLabel={nextLabel}
                    questionPreamble={questionPreamble}
                    questionTitle={step.label}
                    questionDescription={step.description}
                    questionNotice={introNotice && i === 0 ? (
                      <HarbourNotice kind="info">{introNotice}</HarbourNotice>
                    ) : null}
                    disabled={navLocked || !isCurrentStepValid}
                    hideBack={editReturnToReview ? true : (i === 0 && mode === 'questions' && !isHarbourChrome)}
                    disableBack={navLocked}
                    nextLoading={showLoading}
                    nextLoadingLabel={loadingLabel}
                    footerStatus={i === currentIndex ? renderHarbourSaveStatus() : null}
                    submitVariant={isHarbourChrome ? 'harbour' : 'default'}
                    belowCard={isHarbourChrome && i === currentIndex ? (
                      <p className="harbour-body-16 harbour-form-reassure">
                        <HarbourIcon name="clock" size={20} />
                        <span><strong>Your answers save as you go</strong>, you can come back and finish later.</span>
                      </p>
                    ) : null}
                  >
                    {renderStepNode(step)}
                  </QuestionCard>
                );
              }}
            />
          </QuestionViewport>
        </div>
        {navCheckError && (
          <div className="mt-2 text-sm text-red-600 text-center" role="alert">
            {navCheckError}
          </div>
        )}
        {saveError && (isHarbourChrome ? (
          <div className="mt-2">
            <HarbourNotice kind="error" title={SAVE_FAILED_TITLE}>
              <p>{saveError.detail}</p>
              {!saveError.refused && (
                <button type="button" className="underline" onClick={retryLastSave}>Try again</button>
              )}
            </HarbourNotice>
          </div>
        ) : (
          <div className="mt-2 text-sm text-red-600" role="alert">
            <p className="font-semibold">{SAVE_FAILED_TITLE}</p>
            <p>
              {saveError.detail}
              {!saveError.refused && (
                <>
                  {' '}
                  <button type="button" className="underline" onClick={retryLastSave}>Try again</button>
                </>
              )}
            </p>
          </div>
        ))}
      </div>
      {isHarbourChrome && (
        <div className="harbour-bottom-bar">
          {!harbourHideBack && (
            <button
              type="button"
              className="harbour-bottom-bar__back"
              onClick={harbourBack}
              disabled={navLocked}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="harbour-bottom-bar__next"
            onClick={editReturnToReview ? onReturnToReviewGuarded : onNext}
            disabled={navLocked || !isCurrentStepValid}
            aria-busy={navLocked || undefined}
          >
            {harbourNextLabel}
            <HarbourIcon name="arrow-right" size={20} />
          </button>
        </div>
      )}
      {isHarbourChrome ? (
        <div className="harbour-form-header__desktop w-full pb-6" ref={footerRef} />
      ) : (
        <FooterProgress
          ref={footerRef}
          timeRemainingText={timeRemainingText}
          sectionProgress={effectiveSectionProgress}
          prefersReducedMotion={prefersReducedMotion}
        />
      )}

    </div>
  );
}

/**
 * Public entry point: the wizard wrapped in an error boundary.
 *
 * A render crash anywhere inside the form previously unmounted the whole tree
 * silently — the page appeared frozen with no message (one of the UAT "form
 * freezes until refresh" candidates). Wrapping here covers every caller
 * (pre-account, post-account, HFP) without each page needing to remember to
 * add its own boundary.
 */
export default function RmsFormTemplate(props) {
  return (
    <RmsFormErrorBoundary>
      <RmsFormTemplateInner {...props} />
    </RmsFormErrorBoundary>
  );
}


